import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Chip,
  Checkbox,
  IconButton,
  Tabs,
  Tab,
  Avatar,
  MenuItem,
  Switch,
  Stack,
  AlertTitle,
} from '@mui/material';
import {
  Receipt as ReceiptIcon,
  CreditCard as CreditCardIcon,
  AccountBalance as AccountBalanceIcon,
  Print as PrintIcon,
  CheckCircle as CheckCircleIcon,
  Person as PersonIcon,
  Close as CloseIcon,
  ShoppingCart as ShoppingCartIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Delete as DeleteIcon,
  PointOfSale as PointOfSaleIcon,
  Send as SendIcon,
  Smartphone as SmartphoneIcon,
  SwapHoriz as SwapHorizIcon,
  Star as StarIcon,
  Timer as TimerIcon,
} from '@mui/icons-material';
import { collection, query, where, onSnapshot, updateDoc, doc, Timestamp, writeBatch, getDocs, addDoc, runTransaction, setDoc, arrayUnion } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../services/firebase';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { Order, OrderItem, Menu, Product, ProductCategory, Staff, PaymentMethodSetting, PlanOrder } from '../types';
import { getLocalizedProductName } from '../utils/localization';
import { todayBusinessDate, getBusinessDate , resolveBoundaryHour } from '../utils/businessDate';
import {
  calculateTieredExtensionFee,
  extensionFeeBreakdown,
  describeExtensionBreakdown,
  calculateOvertimeMinutes,
  computeEffectiveExtensionPrice,
  DEFAULT_TIERS,
} from '../utils/extensionPricing';
import {
  buildCategoryDistributionMap,
  buildStaffDistributionMap,
  summarizeDistribution,
  calculateItemDistributionBase,
  type CategoryDistributionConfig,
} from '../utils/salesDistribution';
import { getSalesPeriodForDate } from '../utils/salesPeriod';
import { calculateProgressiveRate, calculateIncrementalBack } from '../utils/progressiveBack';
import { calculateBack, validateAllocations, type AttendingCast, type NominationInput } from '../utils/backCalculation';
import { resolveAllowances, summarizeAllowances, describeAllowance } from '../utils/allowance';
import { stripUndefined } from '../utils/firestoreSafe';
import { evaluatePlan, canCreateSlip, PLAN_LABELS } from '../utils/planGate';
import type { AllowanceType, AllowanceInput, AppliedAllowance } from '../utils/allowance';
import { issueSlipNumber } from '../utils/slipNumber';
import { resolveTaxRate, isTaxIncluded as shopTaxIncluded, taxOn, taxRatePercentLabel } from '../utils/taxRate';
import { computeSessionCharge } from '../utils/sessionCharge';
import { TimeChargeSettings } from '../utils/timeCharge';

// 販売スタッフ選択
interface SelectedSalesStaff {
  staffId: string;
  staffName: string;
  shareRate: number;  // 分配率（0-1）
  allocationType: 'percent' | 'amount';  // 配分方式
  allocValue: number;  // 入力値（%なら0-100、金額なら円）
  resolvedAmount: number;  // 計算済み配分金額（円）
}

interface GroupedOrder {
  userId: string;
  userName: string;
  orders: Order[];
  total: number;
}

interface TableOrders {
  tableId: string;
  tableNumber: string;
  orders: Order[];
  groups: GroupedOrder[]; // ユーザーごとのグループ
  total: number;
  guestCount?: {
    male: number;
    female: number;
    total: number;
  };
  /** 卓に着いた時刻。時間制の席料を出すのに要る。 */
  sessionStartTime?: Date;
}

interface POSCartItem {
  menuId: string;
  menuName: string;
  price: number;
  quantity: number;
  category: string;
  imageUrl?: string;
  costInfo?: {
    cost: number;
    baseBackRate: number;
  };
  isCustomItem?: boolean; // 自由入力商品フラグ
}

// デジタルレシート送信先
interface DigitalReceiptRecipient {
  id: string;
  name: string;
  type: 'line' | 'sms';
  lineUserId?: string;
  phoneNumber?: string;
}

type CheckoutMode = 'mobile_order' | 'reservation' | 'pos';

/**
 * 注文の「税を足す前の額」。
 *
 * orders の total は税込で書かれている（客側 QR 注文・代理注文とも
 * subtotal + サービス料 + 税）。会計画面はこれを税抜の小計として扱い、
 * その上にもう一度税を足していた。
 *
 * subtotal を持っていればそれを使う。古い注文で無い場合だけ、
 * その注文が持つ tax を引いて戻す（税率を推測しない）。
 */
function orderSubtotal(order: any): number {
  if (typeof order?.subtotal === 'number' && Number.isFinite(order.subtotal)) {
    return order.subtotal + (Number(order.serviceCharge) || 0);
  }
  const total = Number(order?.total) || 0;
  const tax = Number(order?.tax) || 0;
  return Math.max(0, total - tax);
}

const Checkout: React.FC = () => {
  const { shop, user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [tableOrders, setTableOrders] = useState<TableOrders[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTable, setSelectedTable] = useState<TableOrders | null>(null);
  const [openCheckoutDialog, setOpenCheckoutDialog] = useState(false);

  // テーブル統合会計用
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const [selectedTableIds, setSelectedTableIds] = useState<Set<string>>(new Set());
  const [mergedSourceTableIds, setMergedSourceTableIds] = useState<string[]>([]);

  // 転卓用
  const [transferDialog, setTransferDialog] = useState<{
    open: boolean;
    table: TableOrders | null;
  }>({ open: false, table: null });
  const [allTables, setAllTables] = useState<any[]>([]);
  const [transferring, setTransferring] = useState(false);
  const [checkoutType, setCheckoutType] = useState<'group' | 'individual'>('group');
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [paymentMethod, setPaymentMethod] = useState<string>('cash');
  // 伝票数（会計の件数）による課金の状態。上限を超えても会計は止めない。
  const [slipUsage, setSlipUsage] = useState({
    slipsThisMonth: 0, peakMonthlySlips: 0, employeeCount: 0, shopCount: 1,
  });
  // 手当（同伴・指名・ボトルバック等）
  const [allowanceTypes, setAllowanceTypes] = useState<AllowanceType[]>([]);
  const [allowanceInputs, setAllowanceInputs] = useState<AllowanceInput[]>([]);
  // ツケ（掛け売り）の相手と回収責任者
  const [receivableCustomerName, setReceivableCustomerName] = useState('');
  const [receivablePhone, setReceivablePhone] = useState('');
  const [receivableStaffId, setReceivableStaffId] = useState('');
  const [receivableDueDate, setReceivableDueDate] = useState('');
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethodSetting[]>([]);
  const [receivedAmount, setReceivedAmount] = useState<string>('');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // 割引・クーポン・加算関連
  const [discountType, setDiscountType] = useState<'none' | 'amount' | 'percentage' | 'surcharge'>('none');
  const [discountValue, setDiscountValue] = useState<string>('');
  const [discountReason, setDiscountReason] = useState<string>(''); // クーポンコードや割引理由

  // 予約ベースの会計用
  const [reservationCheckout, setReservationCheckout] = useState<any>(null);

  // POSモード用
  const [checkoutMode, setCheckoutMode] = useState<CheckoutMode>('mobile_order');
  const [posCart, setPosCart] = useState<POSCartItem[]>([]);
  const [menus, setMenus] = useState<Menu[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('all');
  const [customerName, setCustomerName] = useState<string>('');

  // 自由入力商品ダイアログ
  const [customItemDialogOpen, setCustomItemDialogOpen] = useState(false);
  const [customItemName, setCustomItemName] = useState('');
  const [customItemPrice, setCustomItemPrice] = useState('');
  const [customItemHasCost, setCustomItemHasCost] = useState(false);
  const [customItemCost, setCustomItemCost] = useState('');
  const [customItemQuantity, setCustomItemQuantity] = useState(1);

  // レシートプリンター情報
  const [receiptPrinter, setReceiptPrinter] = useState<{ ipAddress: string; port: number } | null>(null);

  // レシート発行設定
  const [printPaperReceipt, setPrintPaperReceipt] = useState(false);  // プリンター設定時にtrueに初期化
  const [sendDigitalReceiptEnabled, setSendDigitalReceiptEnabled] = useState(false);
  const [digitalReceiptRecipients, setDigitalReceiptRecipients] = useState<DigitalReceiptRecipient[]>([]);
  const [selectedDigitalRecipient, setSelectedDigitalRecipient] = useState<string | null>(null);

  // 販売スタッフ機能（原価・バック設定が有効な場合のみ）
  const [staffList, setStaffList] = useState<Staff[]>([]);
  const [selectedSalesStaff, setSelectedSalesStaff] = useState<SelectedSalesStaff[]>([]);

  // プラン注文（会計画面表示用）
  const [activePlanOrders, setActivePlanOrders] = useState<any[]>([]);

  // レシートプリンター情報を取得
  useEffect(() => {
    const fetchReceiptPrinter = async () => {
      if (!shop || !shop.receiptPrinterId) {
        setPrintPaperReceipt(false);
        return;
      }

      try {
        const printerDoc = await getDocs(query(
          collection(db, 'printers'),
          where('shopId', '==', shop.id)
        ));

        const printer = printerDoc.docs.find(doc => doc.id === shop.receiptPrinterId);
        if (printer) {
          const data = printer.data();
          setReceiptPrinter({
            ipAddress: data.ipAddress,
            port: data.port || 9100,
          });
          // プリンターが設定されている場合は紙レシート発行を初期ON
          setPrintPaperReceipt(true);
        }
      } catch (err) {
        console.error('レシートプリンター情報の取得エラー:', err);
      }
    };

    fetchReceiptPrinter();
  }, [shop]);

  // 支払い方法マスタを読み込み
  useEffect(() => {
    if (!shop) return;

    // 店舗に設定された支払い方法がある場合はそれを使用
    if (shop.paymentMethods && shop.paymentMethods.length > 0) {
      const activeMethods = shop.paymentMethods
        .filter(m => m.isActive)
        .sort((a, b) => a.sortOrder - b.sortOrder);
      setPaymentMethods(activeMethods);
      // 最初の支払い方法をデフォルトとして設定
      if (activeMethods.length > 0) {
        setPaymentMethod(activeMethods[0].code);
      }
    } else {
      // デフォルトの支払い方法（後方互換性）
      setPaymentMethods([
        { id: 'default-cash', code: 'cash', name: '現金', type: 'cash', isActive: true, sortOrder: 0 },
        { id: 'default-card', code: 'card', name: 'クレジットカード', type: 'card', isActive: true, sortOrder: 1 },
        { id: 'default-transfer', code: 'transfer', name: '銀行振込', type: 'transfer', isActive: true, sortOrder: 2 },
      ]);
    }
  }, [shop]);

  // 全テーブル取得（転卓用）
  useEffect(() => {
    if (!shop) return;
    const q = query(
      collection(db, 'tables'),
      where('shopId', '==', shop.id),
      where('isActive', '==', true)
    );
    const unsub = onSnapshot(q, (snap) => {
      setAllTables(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [shop]);

  // 原価・バック設定またはインセンティブ設定が有効な場合にスタッフ一覧を読み込み
  useEffect(() => {
    if (!shop?.costBackSettings?.enabled && !shop?.incentiveSettings?.enabled) {
      setStaffList([]);
      return;
    }

    // 売上スタッフは /employees を正とする。
    //
    // 以前はここだけがレガシーの /staff を読んでいた。
    // 勤怠・シフト・給与・スタッフアプリはすべて /employees を使うため、
    // 同じ人が2つの ID を持ち、売上と勤怠が永久に突合しなかった。
    // 実際に MATSURI のデータでは、同一人物が両方に別 ID で存在し、
    // 累進バックの出勤日数が常に 0 になっていた。
    const staffQuery = query(
      collection(db, 'employees'),
      where('shopId', '==', shop.id),
      where('status', '==', 'active')
    );

    const unsubscribe = onSnapshot(staffQuery, (snapshot) => {
      const staffData = snapshot.docs.map(doc => {
        const d = doc.data() as any;
        const fullName = [d.personalInfo?.lastName, d.personalInfo?.firstName]
          .filter(Boolean).join(' ').trim();
        return {
          id: doc.id,
          shopId: d.shopId,
          // 表示名 → 本名 の順。どちらも無ければ従業員番号で見分けられるようにする。
          name: d.displayName?.trim() || fullName || `（名前未設定 ${d.employeeCode || doc.id.slice(0, 6)}）`,
          role: d.role ?? 'staff',
          nominationFee: d.nominationFee ?? null,
          // 累進バックの判定に使う
          monthlySalary: d.monthlySalary ?? d.employmentInfo?.monthlyWage,
          hourlyWage: d.hourlyWage ?? d.employmentInfo?.hourlyWage,
          backRate: d.backRate,
          salesProfitDeductionRate: d.salesProfitDeductionRate,
          isActive: d.status === 'active',
        } as unknown as Staff;
      });

      // 同じ人物が重複登録されている場合に備えて名前で寄せる。
      // ただし「名前未設定」は個別に残す（消すと選べなくなる）。
      const uniqueStaff = staffData.reduce((acc: Staff[], current) => {
        const isUnnamed = current.name.startsWith('（名前未設定');
        if (isUnnamed || !acc.find(s => s.name === current.name)) acc.push(current);
        return acc;
      }, []);

      setStaffList(uniqueStaff.sort((a, b) => a.name.localeCompare(b.name, 'ja')));
    });

    return () => unsubscribe();
  }, [shop]);

  // 当月の伝票数を数える。課金の段の判定に使う。
  useEffect(() => {
    if (!shop) return;
    const load = async () => {
      try {
        const now = new Date();
        const threeMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 2, 1);
        const snap = await getDocs(query(
          collection(db, 'checkouts'),
          where('shopId', '==', shop.id),
          where('createdAt', '>=', Timestamp.fromDate(threeMonthsAgo)),
        ));
        const byMonth: Record<string, number> = {};
        snap.docs.forEach((d) => {
          const t = (d.data() as any).createdAt?.toDate?.();
          if (!t) return;
          const k = `${t.getFullYear()}-${t.getMonth()}`;
          byMonth[k] = (byMonth[k] ?? 0) + 1;
        });
        const thisKey = `${now.getFullYear()}-${now.getMonth()}`;
        setSlipUsage((prev) => ({
          ...prev,
          slipsThisMonth: byMonth[thisKey] ?? 0,
          peakMonthlySlips: Math.max(0, ...Object.values(byMonth)),
        }));
      } catch (err) {
        // 課金の表示だけの用途。失敗しても会計は続けられる。
        console.warn('伝票数の取得に失敗:', err);
      }
    };
    load();
  }, [shop]);

  // 手当マスタを読み込む。
  // 以前は手当が NightPro 側にしか無く、会計にも給与にも入っていなかった。
  useEffect(() => {
    if (!shop) return;
    const q = query(
      collection(db, 'allowanceTypes'),
      where('shopId', '==', shop.id),
      where('isActive', '==', true),
    );
    const unsub = onSnapshot(q, (snap) => {
      const list = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })) as AllowanceType[];
      list.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
      setAllowanceTypes(list);
    });
    return () => unsub();
  }, [shop]);

  // 顧客選択から戻ってきた場合の処理
  useEffect(() => {
    const state = location.state as { selectedCustomer?: { id: string; name: string } } | null;
    if (state?.selectedCustomer) {
      setCustomerName(state.selectedCustomer.name);
      setCheckoutMode('pos');
      // stateをクリアして再度のリロード時に重複しないようにする
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // 商品・カテゴリ一覧を読み込み（POSモード用）
  // モバイルオーダーが有効な場合はproductsを、そうでない場合はmenusを使用
  useEffect(() => {
    if (!shop) return;

    const unsubscribers: (() => void)[] = [];

    // 商品を読み込み（モバイルオーダーON/OFF問わず）
    const productsQuery = query(
      collection(db, 'products'),
      where('shopId', '==', shop.id),
      where('displayStatus', '==', 'available')
    );

    const unsubProducts = onSnapshot(productsQuery, (snapshot) => {
      const productsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
      })) as Product[];
      setProducts(productsData);
    });
    unsubscribers.push(unsubProducts);

    // カテゴリを読み込み
    const categoriesQuery = query(
      collection(db, 'productCategories'),
      where('shopId', '==', shop.id),
      where('displayStatus', '==', 'available')
    );

    const unsubCategories = onSnapshot(categoriesQuery, (snapshot) => {
      const categoriesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
      })) as ProductCategory[];
      categoriesData.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
      setCategories(categoriesData);
    });
    unsubscribers.push(unsubCategories);

    if (!shop.mobileOrderEnabled) {
      // 予約用メニューも読み込み（後方互換性）
      const menusQuery = query(
        collection(db, 'menus'),
        where('shopId', '==', shop.id),
        where('isActive', '==', true)
      );

      const unsubMenus = onSnapshot(menusQuery, (snapshot) => {
        const menusData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate(),
          updatedAt: doc.data().updatedAt?.toDate(),
        })) as Menu[];
        setMenus(menusData);
      });
      unsubscribers.push(unsubMenus);
    }

    return () => unsubscribers.forEach(unsub => unsub());
  }, [shop]);

  // URLパラメータからtableIdを取得（Tables.tsxからの遷移用）
  const [targetTableId, setTargetTableId] = useState<string | null>(null);

  useEffect(() => {
    if (!shop) return;

    // URLパラメータから会計モードを判定
    const urlParams = new URLSearchParams(window.location.search);
    const fromReservation = urlParams.get('from') === 'reservation';
    const fromPOS = urlParams.get('mode') === 'pos';
    const tableIdParam = urlParams.get('tableId');

    // tableIdがあれば保存
    if (tableIdParam) {
      setTargetTableId(tableIdParam);
    }

    if (fromPOS) {
      // POSモード
      setCheckoutMode('pos');
      setLoading(false);
      return; // POSモードの場合は注文監視しない
    }

    if (fromReservation) {
      // 予約ベースの会計：localStorageから予約情報を取得
      const reservationData = localStorage.getItem('checkoutReservation');
      if (reservationData) {
        try {
          const reservation = JSON.parse(reservationData);
          setCheckoutMode('reservation');
          // 予約情報を表示用に変換してダイアログを開く
          handleOpenReservationCheckout(reservation);
        } catch (error) {
          console.error('予約情報の解析エラー:', error);
        }
      }
      return; // 予約ベースの場合は注文監視しない
    }

    // デフォルト: モバイルオーダーモード
    setCheckoutMode('mobile_order');

    console.log('Checkout.tsx: Fetching orders for shop:', shop.id, shop.shopName);

    // 通常のモバイルオーダー会計：未会計の全ての注文を監視
    const q = query(
      collection(db, 'orders'),
      where('shopId', '==', shop.id),
      where('status', 'in', ['pending', 'confirmed', 'preparing', 'ready', 'served'])
    );

    const unsubscribe = onSnapshot(
      q,
      async (snapshot) => {
        console.log('Checkout.tsx: Received orders count:', snapshot.docs.length);
        const ordersData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          orderedAt: doc.data().orderedAt?.toDate(),
          completedAt: doc.data().completedAt?.toDate(),
          printedAt: doc.data().printedAt?.toDate(),
          createdAt: doc.data().createdAt?.toDate(),
          updatedAt: doc.data().updatedAt?.toDate(),
        })) as Order[];

        // テーブルごとにグループ化し、さらにユーザーごとにグループ化
        const tableMap = new Map<string, TableOrders>();
        ordersData.forEach(order => {
          if (!tableMap.has(order.tableId)) {
            tableMap.set(order.tableId, {
              tableId: order.tableId,
              tableNumber: order.tableNumber,
              orders: [],
              groups: [],
              total: 0,
            });
          }
          const tableData = tableMap.get(order.tableId)!;
          tableData.orders.push(order);
          // 注文の total は税込（客側 QR 注文が subtotal + サービス料 + 税 で書く）。
          // ここで積んだ額を calculateSubtotal() が「税抜の小計」として返し、
          // その上に calculateTax() がもう一度税を足していた。
          // 卓1で 小計 ¥29,260（税込）+ 消費税 ¥2,926 = ¥32,186 になり、
          // 客がスマホで見た ¥29,260 との差 ¥2,926 を過大請求していた。
          // 税を足す前の額を積む。
          tableData.total += orderSubtotal(order);
        });

        // ユーザーごとにグループ化
        tableMap.forEach((tableData, tableId) => {
          const userMap = new Map<string, GroupedOrder>();

          tableData.orders.forEach(order => {
            // 認証タイプに応じてユーザーIDと名前を決定
            let userId: string;
            let userName: string;

            if (order.authType === 'line') {
              userId = order.lineUserId || 'guest';
              userName = order.lineDisplayName || 'ゲスト';
            } else if (order.authType === 'sms') {
              userId = order.firebaseUid || order.phoneNumber || 'guest';
              userName = order.phoneNumber || 'ゲスト';
            } else {
              userId = 'guest';
              userName = 'ゲスト';
            }

            if (!userMap.has(userId)) {
              userMap.set(userId, {
                userId,
                userName,
                orders: [],
                total: 0,
              });
            }

            const userGroup = userMap.get(userId)!;
            userGroup.orders.push(order);
            userGroup.total += orderSubtotal(order);
          });

          tableData.groups = Array.from(userMap.values());
        });

        // テーブルセッション情報を取得して来店人数を追加
        const tableEntries = Array.from(tableMap.entries());
        for (const [tableId, tableData] of tableEntries) {
          try {
            const sessionQuery = query(
              collection(db, `shops/${shop.id}/tableSessions`),
              where('tableNumber', '==', tableData.tableNumber),
              where('status', '==', 'active')
            );
            const sessionSnapshot = await getDocs(sessionQuery);
            if (!sessionSnapshot.empty) {
              const sessionData = sessionSnapshot.docs[0].data();
              if (sessionData.guestCount) {
                tableData.guestCount = sessionData.guestCount;
              }
              // 時間制の席料を出すのに、卓に着いた時刻が要る。
              // ここで一緒に持たせておかないと、会計のたびに引き直すことになる。
              const st = sessionData.startTime;
              if (st?.toDate) tableData.sessionStartTime = st.toDate();
              else if (st instanceof Date) tableData.sessionStartTime = st;
            }
          } catch (err) {
            console.error('テーブルセッション情報の取得エラー:', err);
          }
        }

        const tables = Array.from(tableMap.values());
        tables.sort((a, b) => a.tableNumber.localeCompare(b.tableNumber, 'ja', { numeric: true }));
        setTableOrders(tables);
        setLoading(false);
      },
      (error) => {
        console.error('注文取得エラー:', error);
        setError('注文の取得に失敗しました');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [shop]);

  // URLパラメータでtableIdが指定されている場合、そのテーブルの会計ダイアログを自動で開く
  useEffect(() => {
    if (!targetTableId || loading || tableOrders.length === 0) return;

    const targetTable = tableOrders.find(t => t.tableId === targetTableId);
    if (targetTable) {
      // 該当テーブルが見つかったら会計ダイアログを開く
      handleOpenCheckout(targetTable);
      // URLパラメータをクリア（再度開かないように）
      setTargetTableId(null);
      window.history.replaceState({}, document.title, '/mobile-order/checkout');
    } else {
      // 該当テーブルに注文がない場合
      setError(`テーブルに未会計の注文がありません`);
      setTargetTableId(null);
      window.history.replaceState({}, document.title, '/mobile-order/checkout');
    }
  }, [targetTableId, loading, tableOrders]);

  // POSモード: カートにメニューを追加（予約用）
  const handleAddMenuToCart = (menu: Menu) => {
    const existingItem = posCart.find(item => item.menuId === menu.id);
    if (existingItem) {
      setPosCart(posCart.map(item =>
        item.menuId === menu.id
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      setPosCart([...posCart, {
        menuId: menu.id,
        menuName: menu.name,
        price: menu.price,
        quantity: 1,
        category: menu.category,
      }]);
    }
  };

  // POSモード: カートに商品を追加（モバイルオーダー用）
  const handleAddProductToCart = (product: Product) => {
    const existingItem = posCart.find(item => item.menuId === product.id);
    if (existingItem) {
      setPosCart(posCart.map(item =>
        item.menuId === product.id
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      const category = categories.find(c => c.id === product.categoryId);
      setPosCart([...posCart, {
        menuId: product.id,
        menuName: product.name,
        price: product.price,
        quantity: 1,
        category: category?.name || '',
        imageUrl: product.imageUrl,
      }]);
    }
  };

  // POSモード: カートの数量を変更
  const handleUpdateCartQuantity = (menuId: string, quantity: number) => {
    if (quantity <= 0) {
      // 数量が0以下なら削除
      setPosCart(posCart.filter(item => item.menuId !== menuId));
    } else {
      setPosCart(posCart.map(item =>
        item.menuId === menuId
          ? { ...item, quantity }
          : item
      ));
    }
  };

  // POSモード: カートから商品を削除
  const handleRemoveFromCart = (menuId: string) => {
    setPosCart(posCart.filter(item => item.menuId !== menuId));
  };

  // POSモード: カートをクリア
  const handleClearCart = () => {
    setPosCart([]);
    setCustomerName('');
    setDiscountType('none');
    setDiscountValue('');
    setDiscountReason('');
  };

  // 自由入力商品をカートに追加
  const handleAddCustomItem = () => {
    const price = parseFloat(customItemPrice);
    if (!customItemName.trim() || isNaN(price) || price <= 0) return;

    const customId = `custom_${Date.now()}`;
    const cost = customItemHasCost ? (parseFloat(customItemCost) || 0) : 0;

    setPosCart([...posCart, {
      menuId: customId,
      menuName: customItemName.trim(),
      price,
      quantity: customItemQuantity,
      category: '自由入力',
      isCustomItem: true,
      ...(customItemHasCost && cost > 0 ? {
        costInfo: { cost, baseBackRate: 50 },
      } : {}),
    }]);

    // リセット
    setCustomItemName('');
    setCustomItemPrice('');
    setCustomItemHasCost(false);
    setCustomItemCost('');
    setCustomItemQuantity(1);
    setCustomItemDialogOpen(false);
  };

  // POSモード: 会計ダイアログを開く
  const handleOpenPOSCheckout = () => {
    if (posCart.length === 0) {
      setError('カートに商品がありません');
      return;
    }
    const total = posCart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    setPaymentMethod('cash');
    setReceivedAmount(total.toString());
    setDiscountType('none');
    setDiscountValue('');
    setDiscountReason('');
    setOpenCheckoutDialog(true);
  };

  const handleOpenCheckout = (table: TableOrders) => {
    setMergedSourceTableIds([]); // 単一テーブル時はクリア
    setSelectedTable(table);
    setCheckoutType('group');
    setSelectedUsers(new Set());
    setPaymentMethod('cash');
    setReceivedAmount(table.total.toString());
    setDiscountType('none');
    setDiscountValue('');
    setDiscountReason('');

    // デジタルレシート送信先を構築
    const recipients: DigitalReceiptRecipient[] = [];
    const seenIds = new Set<string>();

    table.groups.forEach((group) => {
      group.orders.forEach((order) => {
        // LINE認証ユーザー
        if (order.lineUserId && !seenIds.has(order.lineUserId)) {
          seenIds.add(order.lineUserId);
          recipients.push({
            id: order.lineUserId,
            name: order.lineDisplayName || 'LINEユーザー',
            type: 'line',
            lineUserId: order.lineUserId,
          });
        }
        // SMS認証ユーザー
        if (order.phoneNumber && !seenIds.has(order.phoneNumber)) {
          seenIds.add(order.phoneNumber);
          recipients.push({
            id: order.phoneNumber,
            name: order.phoneNumber,
            type: 'sms',
            phoneNumber: order.phoneNumber,
          });
        }
      });
    });

    setDigitalReceiptRecipients(recipients);
    // 送信先が1件の場合は自動選択
    setSelectedDigitalRecipient(recipients.length === 1 ? recipients[0].id : null);
    setSendDigitalReceiptEnabled(false);

    // プラン注文を取得して表示用にセット
    fetchPlanOrdersForDisplay([table.tableId]);

    setOpenCheckoutDialog(true);
  };

  // プラン注文を表示用に取得
  const fetchPlanOrdersForDisplay = async (tableIds: string[]) => {
    if (!shop) {
      setActivePlanOrders([]);
      return;
    }
    try {
      const allPlanOrders: any[] = [];
      for (const tid of tableIds) {
        // 会計済み（paid）と終了済み（completed）を混ぜてはいけない。
        // 同じ卓の過去の会計まで今回の伝票に積まれ、
        // 45日ぶんが乗って ¥1,638,000 のような請求額になっていた。
        // いま席に付いているぶんだけを見る。
        const planOrdersQuery = query(
          collection(db, 'planOrders'),
          where('shopId', '==', shop.id),
          where('tableId', '==', tid),
          where('status', 'in', ['active', 'last_order_notified'])
        );
        const snapshot = await getDocs(planOrdersQuery);
        snapshot.docs.forEach(d => {
          allPlanOrders.push({ id: d.id, ...d.data() });
        });
      }
      setActivePlanOrders(allPlanOrders);
    } catch (err) {
      console.error('プラン注文の取得に失敗:', err);
      setActivePlanOrders([]);
    }
  };

  // テーブル統合会計: テーブル選択の切り替え
  const handleToggleTableSelection = (tableId: string) => {
    setSelectedTableIds(prev => {
      const next = new Set(prev);
      if (next.has(tableId)) {
        next.delete(tableId);
      } else {
        next.add(tableId);
      }
      return next;
    });
  };

  // 転卓処理
  const handleTransferTable = async (newTable: any) => {
    if (!shop || !transferDialog.table) return;
    setTransferring(true);
    try {
      const functions = getFunctions(undefined, 'asia-northeast1');
      const transfer = httpsCallable(functions, 'transferTable');
      await transfer({
        shopId: shop.id,
        oldTableId: transferDialog.table.tableId,
        newTableId: newTable.id,
        newTableNumber: newTable.tableNumber,
        staffId: user?.uid,
      });
      const fromTable = transferDialog.table.tableNumber;
      setTransferDialog({ open: false, table: null });
      setSuccess(`テーブル${fromTable}→${newTable.tableNumber}に転卓しました`);
    } catch (err: any) {
      setError(err.message || '転卓に失敗しました');
    } finally {
      setTransferring(false);
    }
  };

  // テーブル統合会計: 選択したテーブルを統合して会計ダイアログを開く
  const handleOpenMergedCheckout = () => {
    const tables = tableOrders.filter(t => selectedTableIds.has(t.tableId));
    if (tables.length < 2) {
      setError('統合会計には2つ以上のテーブルが必要です');
      return;
    }

    // テーブル番号順にソート
    tables.sort((a, b) => a.tableNumber.localeCompare(b.tableNumber));

    // ユーザーグループをマージ（同一ユーザーが複数テーブルで注文した場合に統合）
    const userMap = new Map<string, GroupedOrder>();
    tables.forEach(table => {
      table.groups.forEach(group => {
        const existing = userMap.get(group.userId);
        if (existing) {
          existing.orders = [...existing.orders, ...group.orders];
          existing.total += group.total;
        } else {
          userMap.set(group.userId, {
            userId: group.userId,
            userName: group.userName,
            orders: [...group.orders],
            total: group.total,
          });
        }
      });
    });

    // 来店人数を合算
    const hasAnyGuestCount = tables.some(t => t.guestCount);
    const mergedGuestCount = hasAnyGuestCount
      ? tables.reduce(
          (acc, t) => ({
            male: acc.male + (t.guestCount?.male || 0),
            female: acc.female + (t.guestCount?.female || 0),
            total: acc.total + (t.guestCount?.total || 0),
          }),
          { male: 0, female: 0, total: 0 }
        )
      : undefined;

    // 統合されたTableOrdersを構築
    const mergedTable: TableOrders = {
      tableId: tables[0].tableId, // 代表テーブル
      tableNumber: tables.map(t => t.tableNumber).join('+'),
      orders: tables.flatMap(t => t.orders),
      groups: Array.from(userMap.values()),
      total: tables.reduce((sum, t) => sum + t.total, 0),
      guestCount: mergedGuestCount,
    };

    // 元テーブルIDを保存（会計後の全テーブルセッションクリアに使用）
    setMergedSourceTableIds(tables.map(t => t.tableId));

    // 既存のcheckout flowを再利用（handleOpenCheckout内のmergedSourceTableIdsクリアを上書き）
    setSelectedTable(mergedTable);
    setCheckoutType('group');
    setSelectedUsers(new Set());
    setPaymentMethod('cash');
    setReceivedAmount(mergedTable.total.toString());
    setDiscountType('none');
    setDiscountValue('');
    setDiscountReason('');

    // デジタルレシート送信先を構築（全テーブルのユーザーから）
    const recipients: DigitalReceiptRecipient[] = [];
    const seenIds = new Set<string>();
    mergedTable.groups.forEach((group) => {
      group.orders.forEach((order) => {
        if (order.lineUserId && !seenIds.has(order.lineUserId)) {
          seenIds.add(order.lineUserId);
          recipients.push({
            id: order.lineUserId,
            name: order.lineDisplayName || 'LINEユーザー',
            type: 'line',
            lineUserId: order.lineUserId,
          });
        }
        if (order.phoneNumber && !seenIds.has(order.phoneNumber)) {
          seenIds.add(order.phoneNumber);
          recipients.push({
            id: order.phoneNumber,
            name: order.phoneNumber,
            type: 'sms',
            phoneNumber: order.phoneNumber,
          });
        }
      });
    });
    setDigitalReceiptRecipients(recipients);
    setSelectedDigitalRecipient(recipients.length === 1 ? recipients[0].id : null);
    setSendDigitalReceiptEnabled(false);

    // プラン注文を全テーブル分取得して表示用にセット
    fetchPlanOrdersForDisplay(tables.map(t => t.tableId));

    setOpenCheckoutDialog(true);

    // マルチ選択モード解除
    setMultiSelectMode(false);
    setSelectedTableIds(new Set());
  };

  const handleUserSelection = (userId: string) => {
    const newSelected = new Set(selectedUsers);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else {
      newSelected.add(userId);
    }
    setSelectedUsers(newSelected);
  };

  // 予約ベースの会計ダイアログを開く
  const handleOpenReservationCheckout = (reservation: any) => {
    setReservationCheckout(reservation);
    setPaymentMethod('cash');
    setReceivedAmount(reservation.totalPrice.toString());
    setDiscountType('none');
    setDiscountValue('');
    setDiscountReason('');
    setOpenCheckoutDialog(true);
  };

  /**
   * 時間制の席料。
   *
   * 設定していない店では applies=false になり、何も足さない。
   * 数えるのも掛けるのも utils/sessionCharge.ts の1箇所に任せる。
   * ここで分を数えて掛け算を書くと、アプリの卓の画面と額が食い違う。
   */
  const seatCharge = useMemo(() => {
    const settings = (shop as any)?.timeCharge as TimeChargeSettings | undefined;
    if (!settings || !selectedTable?.sessionStartTime) {
      return { applies: false, amount: 0, error: null as string | null, result: null as any, stayMinutes: 0 };
    }
    return computeSessionCharge(settings, {
      startTime: selectedTable.sessionStartTime,
      endTime: new Date(),
      businessDayBoundaryHour: resolveBoundaryHour(shop),
    });
  }, [shop, selectedTable]);

  const calculateSubtotal = () => {
    // POSモードの会計
    if (checkoutMode === 'pos') {
      return posCart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    }

    // 予約ベースの会計
    if (reservationCheckout) {
      return reservationCheckout.totalPrice;
    }

    // モバイルオーダーベースの会計
    if (!selectedTable) return 0;

    if (checkoutType === 'group') {
      // 席料は卓ぜんぶの会計にだけ足す。
      // 客ごとに分けて払うときに人数分の席料を取ると二重取りになる。
      return selectedTable.total + seatCharge.amount;
    } else {
      return selectedTable.groups
        .filter(g => selectedUsers.has(g.userId))
        .reduce((sum, g) => sum + g.total, 0);
    }
  };

  const calculateDiscount = () => {
    const subtotal = calculateSubtotal();

    if (discountType === 'amount') {
      // 固定額割引
      const discount = parseFloat(discountValue) || 0;
      return Math.min(discount, subtotal); // 小計を超えない
    } else if (discountType === 'percentage') {
      // パーセント割引
      const percentage = parseFloat(discountValue) || 0;
      return Math.round(subtotal * (percentage / 100));
    } else if (discountType === 'surcharge') {
      // 加算（マイナス値として返す = 合計に加算される）
      const surcharge = parseFloat(discountValue) || 0;
      return -surcharge; // マイナスにして返す
    }
    return 0;
  };

  // サービス料合計（表示用、注文に含まれている分を集計）
  const calculateServiceChargeTotal = () => {
    if (checkoutMode === 'pos' || reservationCheckout) return 0;
    if (!selectedTable) return 0;
    const orders = checkoutType === 'group'
      ? selectedTable.orders
      : selectedTable.groups.filter(g => selectedUsers.has(g.userId)).flatMap(g => g.orders);
    return orders.reduce((sum: number, order: any) => sum + (order.serviceCharge || 0), 0);
  };

  // 消費税の計算（外税の場合のみ別途表示用）
  const calculateTax = () => {
    if (!shop) return 0;
    const subtotal = calculateSubtotal();
    const discount = calculateDiscount();
    const baseTotal = Math.max(0, subtotal - discount);
    // 税率の既定値は utils/taxRate.ts に集約している。
    // ここで `?? 0`、バック側で `|| 0.1` と別々に書いていたため、
    // 税を取らずに会計したのに税込10%前提で割り戻す食い違いが起きていた。
    return taxOn(baseTotal, shop);
  };

  // 決済手数料の計算（支払い方法ごとのfeeRate対応）
  const calculatePaymentFee = () => {
    const selectedMethod = paymentMethods.find(m => m.code === paymentMethod);
    // 支払い方法のfeeRateを優先、なければ旧shop.creditCardFee（カードのみ）にフォールバック
    const feeRate = selectedMethod?.feeRate
      || (selectedMethod?.type === 'card' ? (shop?.creditCardFee || 0) : 0);
    if (!feeRate) return 0;
    const subtotal = calculateSubtotal();
    const discount = calculateDiscount();
    const tax = calculateTax();
    // 手数料は（小計 - 割引 + 消費税）に対して計算
    const baseWithTax = Math.max(0, subtotal - discount) + tax;
    return Math.round(baseWithTax * feeRate);
  };

  // 現在の支払い方法の手数料率を取得（表示用）
  const getPaymentFeeRate = () => {
    const selectedMethod = paymentMethods.find(m => m.code === paymentMethod);
    return selectedMethod?.feeRate
      || (selectedMethod?.type === 'card' ? (shop?.creditCardFee || 0) : 0);
  };

  /** 付けた手当の合計（客への請求分） */
  const calculateAllowanceTotal = () => {
    if (allowanceInputs.length === 0) return 0;
    const applied = resolveAllowances(allowanceTypes, allowanceInputs, calculateSubtotal());
    return summarizeAllowances(applied).chargeTotal;
  };

  const calculateCheckoutTotal = () => {
    const subtotal = calculateSubtotal();
    const discount = calculateDiscount();
    const tax = calculateTax();
    // 手当（指名・同伴など）は客への請求に乗る
    const baseTotal = Math.max(0, subtotal - discount) + tax + calculateAllowanceTotal();
    return baseTotal + calculatePaymentFee();
  };

  // カテゴリ分配設定マップ（再計算回避）
  const categoryDistributionMap = useMemo(
    () => buildCategoryDistributionMap(categories),
    [categories]
  );
  // スタッフ別 profitDeductionRate オーバーライドマップ
  const staffDistributionMap = useMemo(
    () => buildStaffDistributionMap(staffList as any),
    [staffList]
  );

  // OrderItemに categoryId が無い場合は products から解決
  const resolveItemCategoryId = (item: any): string | undefined => {
    if (item.categoryId) return item.categoryId;
    if (item.productId) {
      const p = products.find(pp => pp.id === item.productId);
      return p?.categoryId;
    }
    return undefined;
  };

  // インセンティブ基準額の計算（インセンティブ対応店舗用）
  // 顧客要望③④⑤対応: カテゴリ別 deductCost / profitDeductionRate を反映
  /**
   * @param extensionReferenceTime 延長料をどの時刻で確定するか。
   *   会計処理からは会計確定時刻を渡す。省略時（画面表示）は現在時刻で、
   *   卓が開いている間は表示が進む＝実態どおりになる。
   */
  const calculateIncentiveBase = (
    orders: { items: any[] }[],
    planOrderDocs?: any[],
    extensionReferenceTime: Date = new Date()
  ) => {
    if (!shop) {
      return {
        grossAmount: 0,
        netSubtotal: 0,
        costDeduction: 0,
        incentiveBase: 0,
        planContribution: 0,
        transfers: new Map<string, { staffName: string; amount: number; reason?: string }>(),
      };
    }

    const taxRate = resolveTaxRate(shop);
    const isTaxIncluded = shopTaxIncluded(shop);
    // 顧客要望: 売上分配の税基準（税抜既定 / 税込もオプション）
    const attributionTaxBase = shop.salesAttributionTaxBase ?? 'taxExcluded';
    const useTaxIncludedBase = attributionTaxBase === 'taxIncluded';

    // 商品アイテム: カテゴリ分配設定を適用してアイテム別小計＋振替集計
    let itemsTotal = 0;
    const enrichedItems: any[] = [];
    orders.forEach(order => {
      (order.items || []).forEach(item => {
        if (item.itemStatus === 'cancelled') return;
        const itemTotal = (item.price || 0) * (item.quantity || 1);
        itemsTotal += itemTotal;
        enrichedItems.push({
          ...item,
          categoryId: resolveItemCategoryId(item),
        });
      });
    });

    const distSummary = summarizeDistribution(
      enrichedItems,
      categoryDistributionMap,
      staffDistributionMap
    );

    // プラン注文の合計（原価なし＝全額インセンティブ対象、階段モード対応）
    let planTotal = 0;
    if (planOrderDocs) {
      planOrderDocs.forEach(planDoc => {
        const data = planDoc.data ? planDoc.data() : planDoc;
        const effExt = computeEffectiveExtensionPrice(data, extensionReferenceTime);
        planTotal += (data.planPrice || 0) + effExt + (data.nominationFee || 0);
      });
    }

    const grossTotal = itemsTotal + planTotal;

    // 税抜化基準。
    //  - useTaxIncludedBase=true のとき、税込のまま使用（÷していない値）
    //  - false (既定) のとき、税抜化
    // 税込価格表示でない (priceDisplayType!='tax_included') 場合、価格自体が既に税抜なので変換不要
    const toBase = (gross: number) => {
      if (useTaxIncludedBase) {
        // 税込ベース: 税込価格表示でなければ税を加算
        return isTaxIncluded ? gross : gross * (1 + taxRate);
      } else {
        // 税抜ベース: 税込価格表示なら÷
        return isTaxIncluded ? gross / (1 + taxRate) : gross;
      }
    };

    const netItemsBase = toBase(distSummary.generalBase);
    const netPlanBase = toBase(planTotal);
    const netSubtotal = toBase(grossTotal);

    // 振替アイテムの基準額 (Map再構築)
    const netTransfers = new Map<string, { staffName: string; amount: number; reason?: string }>();
    distSummary.transfers.forEach((v, k) => {
      netTransfers.set(k, { staffName: v.staffName, amount: Math.round(toBase(v.amount)), reason: v.reason });
    });

    // perStaffBase（per-staff portion 計算結果）も同様に変換
    const netPerStaffBase = new Map<string, { staffName: string; amount: number }>();
    distSummary.perStaffBase.forEach((v, k) => {
      netPerStaffBase.set(k, { staffName: v.staffName, amount: Math.round(toBase(v.amount)) });
    });

    // 通常分配対象: アイテム振替なし分 + プラン全額
    const incentiveBase = netItemsBase + netPlanBase;

    return {
      grossAmount: grossTotal,
      netSubtotal: Math.round(netSubtotal),
      costDeduction: Math.round(distSummary.totalCostDeduction),
      incentiveBase: Math.round(Math.max(0, incentiveBase)),
      planContribution: planTotal,
      transfers: netTransfers,
      perStaffBase: netPerStaffBase,
      attributionTaxBase,
    };
  };

  // 売上配分の金額を解決（金額先引き → 残額をパーセントで分配 → 端数は店へ）
  const resolveAllocations = (staffList: SelectedSalesStaff[], netSales: number): SelectedSalesStaff[] => {
    if (staffList.length === 0 || netSales <= 0) return staffList;

    // 1. 金額指定の合計を先に引く
    let fixedTotal = 0;
    staffList.forEach(s => {
      if (s.allocationType === 'amount') {
        fixedTotal += s.allocValue;
      }
    });
    const remainingForPercent = Math.max(0, netSales - fixedTotal);

    // 2. パーセント指定の合計を計算
    let percentTotal = 0;
    staffList.forEach(s => {
      if (s.allocationType === 'percent') {
        percentTotal += s.allocValue;
      }
    });

    // 3. 各スタッフの配分金額を計算
    let allocatedTotal = 0;
    const resolved = staffList.map(s => {
      let amount: number;
      if (s.allocationType === 'amount') {
        amount = Math.min(s.allocValue, netSales);
      } else {
        // パーセント: remainingForPercent × (自分の% / パーセント合計)
        const effectivePercent = percentTotal > 0 ? s.allocValue / percentTotal : 0;
        amount = Math.floor(remainingForPercent * effectivePercent);
      }
      allocatedTotal += amount;
      return { ...s, resolvedAmount: amount, shareRate: netSales > 0 ? amount / netSales : 0 };
    });

    // 4. 端数（余り）を「店」エントリに加算、なければ最初のスタッフに加算
    const remainder = netSales - allocatedTotal;
    if (remainder !== 0) {
      const shopIdx = resolved.findIndex(s => s.staffId === '__shop__');
      if (shopIdx >= 0) {
        resolved[shopIdx].resolvedAmount += remainder;
        resolved[shopIdx].shareRate = resolved[shopIdx].resolvedAmount / netSales;
      } else {
        // 店エントリがなければ追加
        resolved.push({
          staffId: '__shop__',
          staffName: '店',
          allocationType: 'amount' as const,
          allocValue: remainder,
          resolvedAmount: remainder,
          shareRate: netSales > 0 ? remainder / netSales : 0,
        });
      }
    }

    return resolved;
  };

  /**
   * 卓に付いていたキャストを sessions から読む。
   *
   * ヘルプ・場内で回ったキャストは sessions.helpStaff に記録されるが、
   * 以前は会計側がこのコレクションを一度も読んでいなかったため、
   * 回した子のバックが構造上ゼロになっていた。
   */
  const loadAttendingCasts = async (tableIds: string[]): Promise<AttendingCast[]> => {
    if (!shop || tableIds.length === 0) return [];
    const casts: AttendingCast[] = [];
    const seen = new Set<string>();
    const toDate = (v: any): Date | null =>
      v?.toDate ? v.toDate() : (v instanceof Date ? v : null);

    // v4.0.0 の卓セッション（casts[] に本指名・場内・ヘルプが時間帯付きで入る）
    for (let i = 0; i < tableIds.length; i += 10) {
      const chunk = tableIds.slice(i, i + 10);
      const snap = await getDocs(query(
        collection(db, 'tableSessions'),
        where('shopId', '==', shop.id),
        where('tableId', 'in', chunk),
        where('status', 'in', ['active', 'ordering', 'checkout']),
      ));
      snap.docs.forEach((d) => {
        const data: any = d.data();
        const sessionStart = toDate(data.startTime) ?? new Date();
        (data.casts ?? []).forEach((c: any) => {
          if (!c?.staffId) return;
          const key = `${c.staffId}:${c.assignedAt?.seconds ?? ''}`;
          if (seen.has(key)) return;
          seen.add(key);
          casts.push({
            staffId: c.staffId,
            staffName: c.staffName ?? '',
            role: ['primary', 'banai', 'help', 'back'].includes(c.role) ? c.role : 'help',
            assignedAt: toDate(c.assignedAt) ?? sessionStart,
            releasedAt: toDate(c.releasedAt),
          });
        });
      });
    }
    if (casts.length > 0) return casts;

    // 旧 sessions（primaryStaffId + helpStaff[]）へのフォールバック
    for (let i = 0; i < tableIds.length; i += 10) {
      const chunk = tableIds.slice(i, i + 10);
      const snap = await getDocs(query(
        collection(db, 'sessions'),
        where('shopId', '==', shop.id),
        where('tableId', 'in', chunk),
        where('status', 'in', ['active', 'ordering', 'checkout']),
      ));
      snap.docs.forEach((d) => {
        const data: any = d.data();
        const sessionStart = toDate(data.startTime) ?? new Date();

        if (data.primaryStaffId && !seen.has(`p:${data.primaryStaffId}`)) {
          seen.add(`p:${data.primaryStaffId}`);
          casts.push({
            staffId: data.primaryStaffId,
            staffName: data.primaryStaffName ?? '',
            role: 'primary',
            assignedAt: sessionStart,
            releasedAt: toDate(data.endTime),
          });
        }
        (data.helpStaff ?? []).forEach((h: any) => {
          if (!h?.staffId) return;
          const key = `h:${h.staffId}:${h.assignedAt?.seconds ?? ''}`;
          if (seen.has(key)) return;
          seen.add(key);
          casts.push({
            staffId: h.staffId,
            staffName: h.staffName ?? '',
            role: h.role === 'back' ? 'back' : 'help',
            assignedAt: toDate(h.assignedAt) ?? sessionStart,
            releasedAt: toDate(h.releasedAt),
          });
        });
      });
    }
    return casts;
  };

  /**
   * ツケ（掛け売り）を未収金として起こす。
   *
   * スナックの売上構成では珍しくないが、Firestore 側にも NightPro 側にも
   * この概念が無く、現金として計上するか会計そのものを立てられなかった。
   * 回収責任者をキャストに紐づけておくと、回収率が可視化できる。
   */
  const createReceivable = async (params: {
    checkoutId: string;
    checkoutType: 'table' | 'pos';
    amount: number;
    customerName: string;
    customerId?: string;
    phoneNumber?: string;
    responsibleStaffId?: string;
    responsibleStaffName?: string;
    dueDate?: Date;
    note?: string;
    checkoutAt: Date;
  }) => {
    if (!shop || params.amount <= 0) return null;
    const businessDate = getBusinessDate(
      params.checkoutAt,
      shop.timezone || 'Asia/Tokyo',
      resolveBoundaryHour(shop)
    );
    return addDoc(collection(db, 'receivables'), {
      shopId: shop.id,
      customerId: params.customerId ?? null,
      customerName: params.customerName || '名前未入力',
      phoneNumber: params.phoneNumber ?? null,
      checkoutId: params.checkoutId,
      checkoutType: params.checkoutType,
      businessDate,
      amount: params.amount,
      paidAmount: 0,
      balance: params.amount,
      responsibleStaffId: params.responsibleStaffId ?? null,
      responsibleStaffName: params.responsibleStaffName ?? null,
      approvedBy: user?.uid ?? '',
      approvedByName: user?.email ?? '',
      dueDate: params.dueDate ? Timestamp.fromDate(params.dueDate) : null,
      status: 'open',
      payments: [],
      note: params.note ?? null,
      createdAt: Timestamp.fromDate(params.checkoutAt),
      updatedAt: Timestamp.now(),
    });
  };

  /**
   * この会計でキープボトルを使った / 新たにキープした場合の台帳更新。
   */
  const applyBottleKeeps = async (params: {
    consumedIds: { id: string; remainingPercentAfter: number }[];
    newKeeps: {
      productId?: string;
      productName: string;
      quantity: number;
      price: number;
      staffId?: string;
      staffName?: string;
    }[];
    customerName: string;
    customerId?: string;
    checkoutId: string;
    checkoutAt: Date;
    keepMonths: number;
  }) => {
    if (!shop) return;
    const batch = writeBatch(db);

    for (const c of params.consumedIds) {
      const ref = doc(db, 'bottleKeeps', c.id);
      batch.update(ref, {
        remainingPercent: c.remainingPercentAfter,
        status: c.remainingPercentAfter <= 0 ? 'consumed' : 'kept',
        usageHistory: arrayUnion({
          usedAt: Timestamp.fromDate(params.checkoutAt),
          checkoutId: params.checkoutId,
          remainingPercentAfter: c.remainingPercentAfter,
        }),
        updatedAt: Timestamp.now(),
      });
    }

    for (const k of params.newKeeps) {
      const expires = new Date(params.checkoutAt);
      expires.setMonth(expires.getMonth() + params.keepMonths);
      const ref = doc(collection(db, 'bottleKeeps'));
      batch.set(ref, {
        shopId: shop.id,
        customerId: params.customerId ?? null,
        customerName: params.customerName || '名前未入力',
        productId: k.productId ?? null,
        productName: k.productName,
        quantity: k.quantity,
        openedCheckoutId: params.checkoutId,
        openedAt: Timestamp.fromDate(params.checkoutAt),
        openedByStaffId: k.staffId ?? null,
        openedByStaffName: k.staffName ?? null,
        openedPrice: k.price,
        remainingPercent: 100,
        expiresAt: Timestamp.fromDate(expires),
        status: 'kept',
        usageHistory: [],
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        createdBy: user?.uid ?? '',
      });
    }

    await batch.commit();
  };

  /**
   * インセンティブ記録をFirestoreに保存する。
   *
   * v4.0.0 の変更点:
   *  - 金額の計算を utils/backCalculation.calculateBack に一本化した。
   *    以前は Checkout / PC の承認画面 / アプリの承認画面 / キャストの売上画面が
   *    それぞれ別式で計算し、同じ商品に別の金額を出していた。
   *  - 商品単位で付けたキャストの取り分が計上されるようになった（旧: 計算後に捨てられていた）。
   *  - ヘルプで付いたキャストが sessions から拾われるようになった。
   *  - 指名料が指名されたキャスト本人に付くようになった。
   *  - 延長料は会計確定時刻で固定する（旧: 画面を開いている間ずっと増えていた）。
   *  - 累進の判定と書き込みをトランザクションで囲み、レジ同時締めの競合を防ぐ。
   *  - 累計取得の失敗を握り潰さない（旧: 全員が黙って最低ティアに落ちていた）。
   */
  const saveIncentiveRecords = async (
    checkoutId: string,
    checkoutType: string,
    orders: { items: any[] }[],
    planOrderDocs?: any[],
    actualCheckoutTotal?: number,
    tableIds?: string[],
    checkoutAt: Date = new Date()
  ) => {
    if (!shop?.incentiveSettings?.enabled) return;

    const items = orders.flatMap((o) =>
      (o.items || []).map((it: any) => ({ ...it, categoryId: resolveItemCategoryId(it) }))
    );
    const plans = (planOrderDocs ?? []).map((d: any) => (d.data ? d.data() : d));
    const nominations: NominationInput[] = plans
      .filter((pl: any) => (pl.nominationFee ?? 0) > 0 || pl.nominatedStaffId)
      .map((pl: any) => ({
        staffId: pl.nominatedStaffId,
        staffName: pl.nominatedStaffName,
        type: pl.nominationType,
        fee: pl.nominationFee ?? 0,
      }));

    const attendingCasts = await loadAttendingCasts(tableIds ?? []);

    // 手当（同伴・指名・ボトルバック等）を確定する。
    // percent の手当は会計小計を母数にする。
    const itemsSubtotal = items.reduce(
      (sum: number, it: any) => sum + (it.price ?? 0) * (it.quantity ?? 1), 0
    );
    const appliedAllowances = resolveAllowances(allowanceTypes, allowanceInputs, itemsSubtotal);

    const staffBackRates = new Map<string, number>();
    (staffList as any[]).forEach((st) => {
      if (st?.backRate != null) staffBackRates.set(st.id, st.backRate);
    });

    const result = calculateBack({
      items,
      // 指名料は nominations 側で計上するため、プラン側では二重に数えない
      planOrders: plans.map((pl: any) => ({ ...pl, nominationFee: 0 })),
      attendingCasts,
      selectedSalesStaff,
      nominations,
      allowances: appliedAllowances,
      categoryMap: categoryDistributionMap,
      staffMap: staffDistributionMap,
      staffBackRates,
      defaultBackRate: shop.costBackSettings?.defaultBackRate ?? 0.5,
      taxRate: resolveTaxRate(shop),
      isTaxIncluded: shopTaxIncluded(shop),
      attributionTaxBase: shop.salesAttributionTaxBase ?? 'taxExcluded',
      checkoutAt,
    });

    if (result.lines.size === 0 && result.unassignedBase <= 0) return;

    const tz = shop.timezone || 'Asia/Tokyo';
    const boundary = resolveBoundaryHour(shop);
    const today = todayBusinessDate(tz, boundary);
    // 営業日と同じ基準で期間を出す。以前はここだけ端末ローカル時刻だったため、
    // 締め日の深夜の会計が「日付は前期・期キーは次期」という食い違った1件になっていた。
    const closingDay = shop.salesPeriod?.closingDay ?? 'monthEnd';
    const period = getSalesPeriodForDate(checkoutAt, closingDay, {
      timezone: tz,
      boundaryHour: boundary,
    });

    const staffIds = Array.from(result.lines.keys());

    // 出勤日数は勤怠を正とする。売上が立った日だけを数えると
    // 売れなかった日が分母から消えて日平均が実態より高く出る。
    const workedDaysByStaff = await loadWorkedDays(staffIds, period);

    // 当期累計は専用ドキュメントで持つ。
    // Firestore のクライアントトランザクションはクエリを含められないため、
    // 「クエリで集計 → 判定 → 書き込み」ではレジ同時締めの競合を防げない。
    // 累計を1文書にまとめ、tx.get / tx.set で読み書きすることで初めて排他できる。
    const totalRef = (staffId: string) =>
      doc(db, 'staffPeriodTotals', `${shop.id}__${period.periodKey}__${staffId}`);

    // 累計文書がまだ無いスタッフのための初期値を、既存の明細から作る。
    // v4.0.0 導入前のデータを引き継ぐために必要。
    const seeds = new Map<string, number>();
    const seedSnap = await getDocs(query(
      collection(db, 'staffIncentives'),
      where('shopId', '==', shop.id),
      where('salesPeriodKey', '==', period.periodKey),
    ));
    seedSnap.docs.forEach((d) => {
      const data: any = d.data();
      const sid = data.staffId as string;
      if (!sid || data.isShopShare || data.isUnassigned) return;
      seeds.set(sid, (seeds.get(sid) ?? 0) + (data.staffIncentiveBase ?? 0));
    });

    await runTransaction(db, async (tx) => {
      // 先に全部読む（Firestore のトランザクションは読み取りを書き込みより先に行う必要がある）
      const totalDocs = await Promise.all(staffIds.map((id) => tx.get(totalRef(id))));

      staffIds.forEach((staffId, i) => {
        const line = result.lines.get(staffId)!;
        const totalDoc = totalDocs[i];
        const cumulative = totalDoc.exists()
          ? ((totalDoc.data() as any).sales ?? 0)
          : (seeds.get(staffId) ?? 0);

        const staffData: any = (staffList as any[]).find((st) => st.id === staffId);
        const workedDays = workedDaysByStaff.get(staffId) ?? 0;

        const incremental = calculateIncrementalBack(
          cumulative,
          line.base,
          workedDays,
          {
            id: staffId,
            name: line.staffName,
            role: (staffData?.role as any) ?? 'staff',
            monthlySalary: staffData?.monthlySalary ?? staffData?.employmentInfo?.monthlyWage,
            hourlyWage: staffData?.hourlyWage ?? staffData?.employmentInfo?.hourlyWage,
          },
          shop.incentiveSettings
        );

        tx.set(totalRef(staffId), {
          shopId: shop.id,
          staffId,
          staffName: line.staffName,
          salesPeriodKey: period.periodKey,
          salesPeriodLabel: period.label,
          sales: cumulative + line.base,
          backPaid: ((totalDoc.exists() ? (totalDoc.data() as any).backPaid : 0) ?? 0) + incremental.back,
          workedDays,
          updatedAt: Timestamp.now(),
        }, { merge: true });

        const ref = doc(collection(db, 'staffIncentives'));
        tx.set(ref, {
          shopId: shop.id,
          staffId,
          staffName: line.staffName,
          date: today,
          checkoutId,
          checkoutType,
          checkoutAt: Timestamp.fromDate(checkoutAt),
          grossAmount: result.grossTotal,
          checkoutTotal: actualCheckoutTotal ?? result.grossTotal,
          netSubtotal: result.netSubtotal,
          costDeduction: result.totalCostDeduction,
          incentiveBase: line.base,
          shareRate: result.netSubtotal > 0 ? line.base / result.netSubtotal : 0,
          staffSales: line.sales,
          staffIncentiveBase: line.base,
          backAmount: line.back,
          breakdown: line.breakdown,
          salesPeriodKey: period.periodKey,
          salesPeriodLabel: period.label,
          cumulativeBeforeThis: cumulative,
          workedDays,
          progressiveMode: shop.incentiveSettings?.mode ?? 'retroactive',
          progressiveRate: incremental.rate,
          progressiveTier: incremental.tier,
          progressiveLabel: incremental.label,
          progressiveBackAmount: incremental.back,
          progressiveSegments: incremental.segments,
          isShopShare: false,
          backStatus: 'pending',
          calcVersion: 'v4.0.0',
          createdAt: Timestamp.now(),
        });
      });

      if (result.shopBase > 0) {
        tx.set(doc(collection(db, 'staffIncentives')), {
          shopId: shop.id,
          staffId: '__shop__',
          staffName: '店',
          date: today,
          checkoutId,
          checkoutType,
          checkoutAt: Timestamp.fromDate(checkoutAt),
          grossAmount: result.grossTotal,
          checkoutTotal: actualCheckoutTotal ?? result.grossTotal,
          netSubtotal: result.netSubtotal,
          costDeduction: 0,
          incentiveBase: result.shopBase,
          shareRate: result.netSubtotal > 0 ? result.shopBase / result.netSubtotal : 0,
          staffSales: result.shopBase,
          staffIncentiveBase: result.shopBase,
          backAmount: 0,
          planContribution: 0,
          salesPeriodKey: period.periodKey,
          salesPeriodLabel: period.label,
          isShopShare: true,
          calcVersion: 'v4.0.0',
          createdAt: Timestamp.now(),
        });
      }

      // 分配先が決まらなかった額は店へ寄せず、未割当として残す。
      // バック承認画面がこれを「担当未設定」に出す。
      // 表示に使う項目（売上・小計・原価控除・分配率）も必ず入れる。
      // 入れないと画面側が undefined を 0 として描き、金額が ¥0 に見える。
      if (result.unassignedBase > 0) {
        tx.set(doc(collection(db, 'staffIncentives')), {
          shopId: shop.id,
          staffId: '__unassigned__',
          staffName: '担当未設定',
          date: today,
          checkoutId,
          checkoutType,
          checkoutAt: Timestamp.fromDate(checkoutAt),
          grossAmount: result.grossTotal,
          checkoutTotal: actualCheckoutTotal ?? result.grossTotal,
          netSubtotal: result.netSubtotal,
          costDeduction: result.totalCostDeduction,
          incentiveBase: result.unassignedBase,
          shareRate: result.netSubtotal > 0 ? result.unassignedBase / result.netSubtotal : 0,
          staffSales: result.unassignedBase,
          staffIncentiveBase: result.unassignedBase,
          backAmount: 0,
          planContribution: 0,
          salesPeriodKey: period.periodKey,
          salesPeriodLabel: period.label,
          isShopShare: false,
          isUnassigned: true,
          backStatus: 'unassigned',
          calcVersion: 'v4.0.0',
          createdAt: Timestamp.now(),
        });
      }
    });
  };

  /** 当期の出勤日数を勤怠から数える（累進の日平均判定に使う） */
  const loadWorkedDays = async (
    staffIds: string[],
    period: { start: Date; end: Date }
  ): Promise<Map<string, number>> => {
    const result = new Map<string, number>();
    if (!shop || staffIds.length === 0) return result;
    for (let i = 0; i < staffIds.length; i += 10) {
      const chunk = staffIds.slice(i, i + 10);
      const snap = await getDocs(query(
        collection(db, 'attendances'),
        where('shopId', '==', shop.id),
        where('employeeId', 'in', chunk),
        where('workDate', '>=', Timestamp.fromDate(period.start)),
        where('workDate', '<', Timestamp.fromDate(period.end)),
      ));
      const days: Record<string, Set<string>> = {};
      snap.docs.forEach((d) => {
        const data: any = d.data();
        if (data.status === 'absent') return;
        // businessDate が入っていない旧レコードは workDate から営業日を割り出す
        const key = data.businessDate
          ?? (data.clockIn?.timestamp?.toDate
                ? getBusinessDate(data.clockIn.timestamp.toDate(), shop.timezone || 'Asia/Tokyo', resolveBoundaryHour(shop))
                : data.workDate?.toDate
                  ? getBusinessDate(data.workDate.toDate(), shop.timezone || 'Asia/Tokyo', resolveBoundaryHour(shop))
                  : null);
        const sid = data.employeeId;
        if (!sid || !key) return;
        if (!days[sid]) days[sid] = new Set();
        days[sid].add(key);
      });
      Object.entries(days).forEach(([sid, set]) => result.set(sid, set.size));
    }
    return result;
  };

  const handleCheckout = async () => {
    if (!shop) return;

    // 会計確定時刻をここで1回だけ固定する。
    // 以前は延長料の基準時刻が省略され既定の new Date() になっていたため、
    // レジ待ちが長いほど客の請求が増えていた（表示中も金額が動いた）。
    const checkoutAt = new Date();

    // 現金払いで金額が不足している場合
    const checkoutTotal = calculateCheckoutTotal();
    // 配分の入力が壊れていたら確定させない
    if (selectedSalesStaff.length > 0) {
      const info = checkoutMode === 'pos'
        ? calculateIncentiveBase([{ items: posCart }], undefined, checkoutAt)
        : calculateIncentiveBase(selectedTable?.orders || [], undefined, checkoutAt);
      const v = validateAllocations(
        selectedSalesStaff.map(st => ({
          staffId: st.staffId, staffName: st.staffName,
          allocationType: st.allocationType, allocValue: st.allocValue,
        })),
        info.incentiveBase
      );
      if (!v.ok) {
        alert('売上配分の設定を確認してください。\n\n' + v.errors.join('\n'));
        return;
      }
    }

    // ツケは相手が特定できないと債権にならない
    const isReceivable = paymentMethods.find((m) => m.code === paymentMethod)?.type === 'receivable';
    if (isReceivable && !receivableCustomerName.trim()) {
      alert('ツケの相手（お客様名）を入力してください');
      return;
    }

    if (paymentMethod === 'cash' && parseFloat(receivedAmount) < checkoutTotal) {
      setError('お預かり金額が不足しています');
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      // 予約ベースの会計
      if (reservationCheckout) {
        const subtotal = calculateSubtotal();
        const discount = calculateDiscount();

        // 予約ドキュメントを更新
        const reservationRef = doc(db, 'reservations', reservationCheckout.id);
        await updateDoc(reservationRef, {
          'payment.status': 'completed',
          'payment.amount': checkoutTotal,
          'payment.paidAt': Timestamp.now(),
          paymentMethod,
          receivedAmount: paymentMethod === 'cash' ? parseFloat(receivedAmount) : checkoutTotal,
          // 割引情報を保存
          discount: discount > 0 ? {
            type: discountType,
            value: parseFloat(discountValue),
            amount: discount,
            reason: discountReason || undefined,
          } : undefined,
          updatedAt: Timestamp.now(),
        });

        // 予約ベースの会計完了後、レシート印刷
        if (shop.receiptPrinterId) {
          try {
            await printReceiptForReservation(reservationCheckout);
          } catch (printError: any) {
            console.error('レシート印刷エラー:', printError);
          }
        }

        setSuccess(
          `${reservationCheckout.userName}様の会計が完了しました` +
          (shop.receiptPrinterId ? '（レシート印刷済み）' : '')
        );
        setTimeout(() => {
          setSuccess(null);
          // localStorageをクリア
          localStorage.removeItem('checkoutReservation');
          // 予約一覧画面に戻る
          window.location.href = `${process.env.PUBLIC_URL || ''}/reservations`;
        }, 2000);

        setOpenCheckoutDialog(false);
        setReservationCheckout(null);
        setPaymentMethod('cash');
        setReceivedAmount('');
        setAllowanceInputs([]);
        setDiscountType('none');
        setDiscountValue('');
        setDiscountReason('');
        return;
      }

      // POSモードの会計
      if (checkoutMode === 'pos') {
        const subtotal = calculateSubtotal();
        const discount = calculateDiscount();

        // POS会計データをFirestoreに保存（記録用）
        const posCheckoutData = {
          shopId: shop.id,
          type: 'pos',
          customerName: customerName || '匿名',
          items: posCart,
          subtotal,
          tax: calculateTax(),
          paymentFee: calculatePaymentFee(), paymentFeeRate: getPaymentFeeRate(), creditCardFee: calculatePaymentFee() || undefined,
          discount: discount > 0 ? {
            type: discountType,
            value: parseFloat(discountValue),
            amount: discount,
            reason: discountReason || undefined,
          } : undefined,
          total: checkoutTotal,
          paymentMethod,
          receivedAmount: paymentMethod === 'cash' ? parseFloat(receivedAmount) : checkoutTotal,
          change: paymentMethod === 'cash' ? Math.max(0, parseFloat(receivedAmount) - checkoutTotal) : 0,
          createdAt: Timestamp.now(),
        };

        // 売上配分情報
        const posIncentiveInfo = calculateIncentiveBase([{ items: posCart }], undefined, checkoutAt);
        const posNetSales = posIncentiveInfo.incentiveBase;
        const posResolved = selectedSalesStaff.length > 0
          ? resolveAllocations(selectedSalesStaff, posNetSales)
          : [{ staffId: '__shop__', staffName: '店', allocationType: 'amount' as const, allocValue: posNetSales, resolvedAmount: posNetSales, shareRate: 1 }];

        (posCheckoutData as any).salesAllocation = {
          subtotal: posIncentiveInfo.netSubtotal,
          totalCost: posIncentiveInfo.costDeduction,
          netSales: posNetSales,
          allocations: posResolved.map(s => ({
            staffId: s.staffId,
            staffName: s.staffName,
            type: s.allocationType,
            value: s.allocValue,
            resolved: s.resolvedAmount,
          })),
        };
        (posCheckoutData as any).salesStatus = 'pending';

        // 後方互換: salesStaff
        (posCheckoutData as any).salesStaff = posResolved.map(s => ({
          staffId: s.staffId,
          staffName: s.staffName,
          shareRate: s.shareRate,
          backRate: shop?.costBackSettings?.defaultBackRate || 0.5,
        }));

        const posCheckoutDoc = await addDoc(collection(db, 'posCheckouts'), stripUndefined(posCheckoutData));

        // インセンティブ記録（POS）
        if (shop?.incentiveSettings?.enabled) {
          await saveIncentiveRecords(
            posCheckoutDoc.id, 'pos', [{ items: posCart }], undefined, checkoutTotal, [], checkoutAt
          );
        }

        if (isReceivable) {
          const resp = staffList.find((st) => st.id === receivableStaffId);
          await createReceivable({
            checkoutId: posCheckoutDoc.id,
            checkoutType: 'pos',
            amount: checkoutTotal,
            customerName: receivableCustomerName.trim(),
            phoneNumber: receivablePhone.trim() || undefined,
            responsibleStaffId: receivableStaffId || undefined,
            responsibleStaffName: resp?.name,
            dueDate: receivableDueDate ? new Date(receivableDueDate) : undefined,
            checkoutAt,
          });
        }

        // POSの会計完了後、レシート印刷
        if (shop.receiptPrinterId) {
          try {
            await printReceiptForPOS();
          } catch (printError: any) {
            console.error('レシート印刷エラー:', printError);
          }
        }

        setSuccess(
          '会計が完了しました' +
          (shop.receiptPrinterId ? '（レシート印刷済み）' : '')
        );
        setTimeout(() => setSuccess(null), 3000);

        setOpenCheckoutDialog(false);
        handleClearCart(); // カートをクリア
        setPaymentMethod('cash');
        setReceivedAmount('');
        setAllowanceInputs([]);
        setSelectedSalesStaff([]);  // 販売スタッフ選択をリセット
        return;
      }

      // モバイルオーダーベースの会計
      if (!selectedTable) return;

      // 個別会計で誰も選択されていない場合
      if (checkoutType === 'individual' && selectedUsers.size === 0) {
        setError('会計する人を選択してください');
        setProcessing(false);
        return;
      }

      // 会計対象の注文を取得
      let ordersToCheckout: Order[] = [];

      if (checkoutType === 'group') {
        // 全員まとめて会計
        ordersToCheckout = selectedTable.orders;
      } else {
        // 個別会計（選択されたユーザーの注文のみ）
        ordersToCheckout = selectedTable.groups
          .filter(g => selectedUsers.has(g.userId))
          .flatMap(g => g.orders);
      }

      const batch = writeBatch(db);

      const subtotal = calculateSubtotal();
      const discount = calculateDiscount();

      // 売上配分は「会計1件」に対して1回だけ計算する。
      // 以前はこの計算が注文ごとのループの中にあり、卓の全額配分が
      // 注文の枚数ぶん複製されて各 order に書かれていた。
      // 3枚の伝票がある卓なら、分析もバック承認も3重に数えていた。
      const tableIncentiveInfo = calculateIncentiveBase(ordersToCheckout, undefined, checkoutAt);
      const tableNetSales = tableIncentiveInfo.incentiveBase;
      const tableResolved = selectedSalesStaff.length > 0
        ? resolveAllocations(selectedSalesStaff, tableNetSales)
        : [{ staffId: '__shop__', staffName: '店', allocationType: 'amount' as const, allocValue: tableNetSales, resolvedAmount: tableNetSales, shareRate: 1 }];

      const salesAllocation = {
        subtotal: tableIncentiveInfo.netSubtotal,
        totalCost: tableIncentiveInfo.costDeduction,
        netSales: tableNetSales,
        allocations: tableResolved.map(s => ({
          staffId: s.staffId,
          staffName: s.staffName,
          type: s.allocationType,
          value: s.allocValue,
          resolved: s.resolvedAmount,
        })),
      };
      const salesStaffCompat = tableResolved.map(s => ({
        staffId: s.staffId,
        staffName: s.staffName,
        shareRate: s.shareRate,
        backRate: shop?.costBackSettings?.defaultBackRate ?? 0.5,
      }));

      // 手当を確定する（会計文書とインセンティブの両方で同じものを使う）
      const itemsSubtotalForAllowance = ordersToCheckout
        .flatMap((o) => o.items || [])
        .reduce((sum: number, it: any) => sum + (it.price ?? 0) * (it.quantity ?? 1), 0);
      const appliedAllowancesForCheckout = resolveAllowances(
        allowanceTypes, allowanceInputs, itemsSubtotalForAllowance
      );
      const allowanceSummaryForCheckout = summarizeAllowances(appliedAllowancesForCheckout);

      // 会計そのものを表す文書を1件つくる。
      //
      // これが無かったのが、この製品でいちばん影響の大きい欠陥だった。
      // /checkouts を読む画面は バック承認・売上分析・顧客の利用履歴・
      // スタッフアプリの「自分のシフト」の4つあるのに、
      // 書き込む経路がコードベース全体で1つも存在しなかった。
      // テーブル会計は orders を更新するだけだったので、
      // 会計しても上の4画面のどれにも出てこなかった。
      const tableCheckoutRef = doc(collection(db, 'checkouts'));
      const businessDateForCheckout = getBusinessDate(
        checkoutAt,
        shop.timezone || 'Asia/Tokyo',
        resolveBoundaryHour(shop)
      );

      // 伝票番号を採番する。
      // 番号が無いと、取り消したときに「どの伝票が消えたか」が分からない。
      // レジが複数あっても重ならないよう、営業日ごとのカウンタで進める。
      const { slipNumber, seq: slipSeq } = await issueSlipNumber(
        db, shop.id, businessDateForCheckout
      );

      batch.set(tableCheckoutRef, stripUndefined({
        shopId: shop.id,
        type: 'table',
        slipNumber,
        slipSeq,
        checkoutType,
        tableId: selectedTable.tableId,
        tableNumber: selectedTable.tableNumber,
        tableName: selectedTable.tableNumber ? `テーブル${selectedTable.tableNumber}` : null,
        mergedTableIds: mergedSourceTableIds.length > 0 ? mergedSourceTableIds : null,
        orderIds: ordersToCheckout.map(o => o.id),
        items: ordersToCheckout.flatMap(o => o.items || []),
        guestCount: selectedTable.guestCount ?? null,
        businessDate: businessDateForCheckout,
        subtotal,
        tax: calculateTax(),
        paymentFee: calculatePaymentFee(),
        paymentFeeRate: getPaymentFeeRate(),
        discount: discount > 0 ? {
          type: discountType,
          value: parseFloat(discountValue),
          amount: discount,
          reason: discountReason || null,
        } : null,
        total: checkoutTotal,
        paymentMethod,
        receivedAmount: paymentMethod === 'cash' ? parseFloat(receivedAmount) : checkoutTotal,
        change: paymentMethod === 'cash' ? Math.max(0, parseFloat(receivedAmount) - checkoutTotal) : 0,
        salesAllocation,
        salesStaff: salesStaffCompat,
        // 付けた手当をそのまま残す。レシートと給与の根拠になる。
        allowances: appliedAllowancesForCheckout,
        allowanceTotal: allowanceSummaryForCheckout.chargeTotal,
        allowanceBackTotal: allowanceSummaryForCheckout.backTotal,
        salesStatus: 'pending',
        status: 'completed',
        createdAt: Timestamp.fromDate(checkoutAt),
        updatedAt: Timestamp.fromDate(checkoutAt),
        createdBy: user?.uid ?? '',
      }));

      // 注文を会計済み（completed）に更新
      ordersToCheckout.forEach(order => {
        const orderRef = doc(db, 'orders', order.id);
        const updateData: any = {
          status: 'completed',
          paymentStatus: 'paid',
          completedAt: Timestamp.fromDate(checkoutAt),
          updatedAt: Timestamp.fromDate(checkoutAt),
          paymentMethod,
          receivedAmount: paymentMethod === 'cash' ? parseFloat(receivedAmount) : checkoutTotal,
          // どの会計に属するかを持たせる。会計から伝票へ、伝票から会計へ辿れる。
          checkoutId: tableCheckoutRef.id,
          businessDate: businessDateForCheckout,
        };
        // 割引がある場合のみ割引情報を追加
        if (discount > 0) {
          updateData.discount = {
            type: discountType,
            value: parseFloat(discountValue),
            amount: discount,
            reason: discountReason || null,
          };
        }
        // 配分は会計文書が正。order 側は参照用に会計IDだけ持たせ、
        // 金額の重複計上が起きないよう salesAllocation は載せない。
        updateData.salesStatus = 'pending';

        batch.update(orderRef, updateData);
      });

      // 全員会計の場合はプラン注文も会計済みに更新
      let planOrderDocs: any[] = [];
      if (checkoutType === 'group') {
        // 統合会計の場合は全テーブル分をクエリ
        const tableIdsForPlanOrders = mergedSourceTableIds.length > 0
          ? mergedSourceTableIds
          : [selectedTable.tableId];

        for (const tid of tableIdsForPlanOrders) {
          // 会計済み（paid）と終了済み（completed）を混ぜてはいけない。
          // ここは表示ではなく、実際に請求する額とバックを決める側。
          // 同じ卓の過去の会計まで積まれ、伝票 ¥71,995 の会計で
          // バック合計 ¥43,080,530 が記録されていた。
          const planOrdersQuery = query(
            collection(db, 'planOrders'),
            where('shopId', '==', shop.id),
            where('tableId', '==', tid),
            where('status', 'in', ['active', 'last_order_notified'])
          );
          const planOrdersSnapshot = await getDocs(planOrdersQuery);
          planOrderDocs.push(...planOrdersSnapshot.docs);
          planOrdersSnapshot.docs.forEach(planDoc => {
            const planRef = doc(db, 'planOrders', planDoc.id);
            // checkoutAdjustment はFlutterアプリ側で設定される可能性があるため上書きしない
            batch.update(planRef, {
              status: 'paid',
              paymentMethod,
              paidAt: Timestamp.now(),
              updatedAt: Timestamp.now(),
            });
          });
        }
      }

      await batch.commit();

      // インセンティブ記録（テーブル会計）
      // 売上スタッフが明示指定されていなくても、卓に付いていたキャストへ分配する。
      // 以前はここで打ち切っていたため、指定を忘れた卓のバックが丸ごと消えていた。
      if (shop?.incentiveSettings?.enabled) {
        const incentiveTableIds = mergedSourceTableIds.length > 0
          ? mergedSourceTableIds
          : [selectedTable.tableId];
        await saveIncentiveRecords(
          tableCheckoutRef.id, 'table', ordersToCheckout, planOrderDocs, checkoutTotal,
          incentiveTableIds, checkoutAt
        );
      }

      if (isReceivable) {
        const resp = staffList.find((st) => st.id === receivableStaffId);
        await createReceivable({
          checkoutId: tableCheckoutRef.id,
          checkoutType: 'table',
          amount: checkoutTotal,
          customerName: receivableCustomerName.trim(),
          phoneNumber: receivablePhone.trim() || undefined,
          responsibleStaffId: receivableStaffId || undefined,
          responsibleStaffName: resp?.name,
          dueDate: receivableDueDate ? new Date(receivableDueDate) : undefined,
          checkoutAt,
        });
      }

      // テーブルのセッションをクリア（次のお客様のために）
      const tableIdsToClear = mergedSourceTableIds.length > 0
        ? mergedSourceTableIds
        : [selectedTable.tableId];

      if (checkoutType === 'group') {
        // グループ会計: 全員分なのでそのままクリア
        for (const tid of tableIdsToClear) {
          try {
            const tableRef = doc(db, 'tables', tid);
            await updateDoc(tableRef, {
              currentSessionId: null,
              sessionStartedAt: null,
              status: 'available',
              updatedAt: Timestamp.now(),
            });
            console.log('テーブルセッションをクリアしました:', tid);
          } catch (tableError: any) {
            console.error('テーブルセッションクリアエラー:', tid, tableError);
          }
        }
      } else {
        // 個別会計: 全注文が支払い済みかチェックし、残りがなければクリア
        try {
          const allOrders = selectedTable.orders;
          const paidOrderIds = new Set(ordersToCheckout.map(o => o.id));
          const remainingOrders = allOrders.filter(o =>
            !paidOrderIds.has(o.id) && (o.status as string) !== 'paid' && o.status !== 'completed'
          );

          if (remainingOrders.length === 0) {
            for (const tid of tableIdsToClear) {
              try {
                const tableRef = doc(db, 'tables', tid);
                await updateDoc(tableRef, {
                  currentSessionId: null,
                  sessionStartedAt: null,
                  status: 'available',
                  updatedAt: Timestamp.now(),
                });
                console.log('全員会計済みのためテーブルセッションをクリア:', tid);
              } catch (tableError: any) {
                console.error('テーブルセッションクリアエラー:', tid, tableError);
              }
            }
          }
        } catch (checkError: any) {
          console.error('残注文チェックエラー:', checkError);
        }
      }

      // レシート発行処理
      const receiptActions: string[] = [];
      let paperIssued = false;
      let digitalIssued = false;

      // 紙レシート印刷
      if (printPaperReceipt && shop.receiptPrinterId) {
        try {
          await printReceiptAuto(ordersToCheckout);
          receiptActions.push('紙レシート印刷済み');
          paperIssued = true;
        } catch (printError: any) {
          console.error('レシート印刷エラー:', printError);
          // 印刷失敗してもエラーは出さず、会計は完了させる
        }
      }

      // デジタルレシート送信
      if (sendDigitalReceiptEnabled && selectedDigitalRecipient) {
        try {
          await sendDigitalReceiptToCustomer(ordersToCheckout, selectedTable.tableNumber);
          receiptActions.push('デジタルレシート送信済み');
          digitalIssued = true;
        } catch (sendError: any) {
          console.error('デジタルレシート送信エラー:', sendError);
          // 送信失敗してもエラーは出さず、会計は完了させる
        }
      }

      // 顧客要望⑥: 領収書発行済みフラグを各注文に書き込む（削除保護用）
      if (paperIssued || digitalIssued) {
        const issuedType = paperIssued && digitalIssued
          ? 'both'
          : paperIssued ? 'paper' : 'digital';
        const issueBatch = writeBatch(db);
        for (const ord of ordersToCheckout) {
          issueBatch.update(doc(db, 'orders', ord.id), {
            receiptIssued: true,
            receiptIssuedAt: Timestamp.now(),
            receiptIssuedBy: user?.uid ?? null,
            receiptIssuedType: issuedType,
          });
        }
        try { await issueBatch.commit(); } catch (e) {
          console.error('領収書発行フラグ書き込みエラー:', e);
        }
      }

      setSuccess(
        `${selectedTable.tableNumber}の会計が完了しました` +
        (receiptActions.length > 0 ? `（${receiptActions.join('、')}）` : '')
      );
      setTimeout(() => setSuccess(null), 3000);
      setOpenCheckoutDialog(false);
      setSelectedTable(null);
      setPaymentMethod('cash');
      setReceivedAmount('');
      setCheckoutType('group');
      setSelectedUsers(new Set());
      setDiscountType('none');
      setDiscountValue('');
      setDiscountReason('');
      setSelectedSalesStaff([]);  // 販売スタッフ選択をリセット
      // レシート発行設定をリセット
      setSendDigitalReceiptEnabled(false);
      setSelectedDigitalRecipient(null);
      setDigitalReceiptRecipients([]);
      // 統合会計のリセット
      setMergedSourceTableIds([]);
      setMultiSelectMode(false);
      setSelectedTableIds(new Set());
    } catch (error: any) {
      console.error('会計エラー:', error);
      setError(`会計処理に失敗しました: ${error.message}`);
    } finally {
      setProcessing(false);
    }
  };

  // 自動レシート印刷（会計完了時）
  const printReceiptAuto = async (orders: Order[]) => {
    if (!selectedTable || !shop || !shop.receiptPrinterId || !receiptPrinter) return;

    const subtotal = calculateSubtotal();
    const discount = calculateDiscount();
    const checkoutTotal = calculateCheckoutTotal();

    // 注文データを整形（キャンセル済みアイテムを除外）
    const ordersData = orders.map(order => ({
      orderNumber: order.orderNumber || '',
      items: order.items
        .filter(item => (item as any).itemStatus !== 'cancelled')
        .map(item => ({
          productName: item.productName,
          quantity: item.quantity,
          price: item.unitPrice || item.price,
          selectedOptions: item.selectedOptions,
        })),
    }));

    // 支払い方法の表示名を取得
    const selectedPaymentMethod = paymentMethods.find(m => m.code === paymentMethod);
    const paymentMethodLabel = selectedPaymentMethod?.name || paymentMethod;
    const isCashPayment = selectedPaymentMethod?.type === 'cash';

    const printData = {
      printerId: shop.receiptPrinterId,
      printerIpAddress: receiptPrinter.ipAddress,
      printerPort: receiptPrinter.port,
      shopName: shop.shopName || '',
      shopAddress: shop.address || '',
      shopPhone: shop.phoneNumber || '',
      tableNumber: selectedTable.tableNumber,
      orders: ordersData,
      subtotal: orders.reduce((sum, order) => sum + order.subtotal, 0),
      serviceCharge: orders.reduce((sum, order) => sum + (order.serviceCharge || 0), 0),
      paymentFee: calculatePaymentFee(), paymentFeeRate: getPaymentFeeRate(), creditCardFee: calculatePaymentFee(),
      tax: orders.reduce((sum, order) => sum + order.tax, 0),
      // 割引情報
      discount: discount > 0 ? {
        type: discountType,
        value: parseFloat(discountValue),
        amount: discount,
        reason: discountReason || undefined,
      } : undefined,
      total: checkoutTotal,
      paymentMethod: paymentMethodLabel, // 表示名
      paymentMethodCode: paymentMethod, // コード（内部用）
      receivedAmount: isCashPayment ? parseFloat(receivedAmount) : checkoutTotal,
      change: isCashPayment ? Math.max(0, parseFloat(receivedAmount) - checkoutTotal) : 0,
      guestCount: selectedTable.guestCount || null,
      // レシート設定
      receiptSettings: shop.receiptSettings || {},
    };

    // ローカルプリンターサーバーを呼び出し
    const response = await fetch('http://localhost:3100/api/print/receipt', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(printData),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'レシート印刷に失敗しました');
    }
  };

  // 予約の会計レシート印刷
  const printReceiptForReservation = async (reservation: any) => {
    if (!shop || !shop.receiptPrinterId) return;

    const subtotal = calculateSubtotal();
    const discount = calculateDiscount();
    const checkoutTotal = calculateCheckoutTotal();

    // 支払い方法の表示名を取得（マスタから）
    const selectedPaymentMethod = paymentMethods.find(m => m.code === paymentMethod);
    const paymentMethodLabel = selectedPaymentMethod?.name || paymentMethod;
    const isCashPayment = selectedPaymentMethod?.type === 'cash';

    // 予約日時のフォーマット
    const reservationDateTime = reservation.reservationDate?.toDate
      ? reservation.reservationDate.toDate()
      : new Date(reservation.reservationDate);
    const dateStr = reservationDateTime.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

    const printData = {
      printerId: shop.receiptPrinterId,
      type: 'reservation', // 予約会計のタイプ
      customerName: reservation.userName,
      reservationDate: dateStr,
      reservationTime: `${reservation.startTime} - ${reservation.endTime}`,
      menuName: reservation.menuName,
      staffName: reservation.staffName || '指名なし',
      subtotal: subtotal,
      paymentFee: calculatePaymentFee(), paymentFeeRate: getPaymentFeeRate(), creditCardFee: calculatePaymentFee(),
      discount: discount > 0 ? {
        type: discountType,
        value: parseFloat(discountValue),
        amount: discount,
        reason: discountReason || undefined,
      } : undefined,
      total: checkoutTotal,
      paymentMethod: paymentMethodLabel,
      paymentMethodCode: paymentMethod,
      receivedAmount: isCashPayment ? parseFloat(receivedAmount) : checkoutTotal,
      change: isCashPayment ? Math.max(0, parseFloat(receivedAmount) - checkoutTotal) : 0,
      // レシート設定
      receiptSettings: shop.receiptSettings || {},
    };

    // ローカルプリンターサーバーを呼び出し
    const response = await fetch('http://localhost:3100/api/print/receipt', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(printData),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'レシート印刷に失敗しました');
    }
  };

  // デジタルレシート送信
  const sendDigitalReceiptToCustomer = async (orders: Order[], tableNumber?: string) => {
    if (!shop || !selectedDigitalRecipient) return;

    const recipient = digitalReceiptRecipients.find(r => r.id === selectedDigitalRecipient);
    if (!recipient) return;

    const subtotal = calculateSubtotal();
    const discount = calculateDiscount();
    const checkoutTotal = calculateCheckoutTotal();

    // 支払い方法の表示名を取得
    const selectedPaymentMethod = paymentMethods.find(m => m.code === paymentMethod);
    const paymentMethodLabel = selectedPaymentMethod?.name || paymentMethod;

    // 注文アイテムを集計（キャンセル済みを除外）
    const itemsMap = new Map<string, { productName: string; quantity: number; price: number }>();
    orders.forEach(order => {
      order.items.forEach(item => {
        if ((item as any).itemStatus === 'cancelled') return;
        const key = `${item.productName}_${item.unitPrice || item.price}`;
        const existing = itemsMap.get(key);
        if (existing) {
          existing.quantity += item.quantity;
        } else {
          itemsMap.set(key, {
            productName: item.productName,
            quantity: item.quantity,
            price: item.unitPrice || item.price,
          });
        }
      });
    });

    const receiptData = {
      shopName: shop.shopName || '',
      items: Array.from(itemsMap.values()),
      subtotal,
      tax: orders.reduce((sum, order) => sum + order.tax, 0),
      serviceCharge: orders.reduce((sum, order) => sum + (order.serviceCharge || 0), 0),
      paymentFee: calculatePaymentFee(), paymentFeeRate: getPaymentFeeRate(), creditCardFee: calculatePaymentFee(),
      discount: discount > 0 ? {
        type: discountType,
        amount: discount,
        reason: discountReason || undefined,
      } : undefined,
      total: checkoutTotal,
      paymentMethod: paymentMethodLabel,
      tableNumber: tableNumber,
      paidAt: new Date().toISOString(),
    };

    // Cloud Function呼び出し
    const functions = getFunctions(undefined, 'asia-northeast1');

    if (recipient.type === 'line') {
      // LINE送信
      const sendDigitalReceipt = httpsCallable(functions, 'sendDigitalReceipt');
      await sendDigitalReceipt({
        shopId: shop.id,
        recipientType: 'line',
        recipientId: recipient.lineUserId,
        receiptData,
      });
    } else if (recipient.type === 'sms') {
      // SMS送信
      const sendSmsNotification = httpsCallable(functions, 'sendSmsNotification');

      // レシートメッセージを生成（簡易版）
      let smsMessage = `【${shop.shopName}】\nご利用ありがとうございます。\n`;
      smsMessage += `合計: ¥${checkoutTotal.toLocaleString()}\n`;
      smsMessage += `お支払い: ${paymentMethodLabel}`;

      await sendSmsNotification({
        shopId: shop.id,
        phoneNumber: recipient.phoneNumber,
        message: smsMessage,
      });
    }

    console.log('デジタルレシート送信完了:', recipient);
  };

  // POSモードのレシート印刷
  const printReceiptForPOS = async () => {
    if (!shop || !shop.receiptPrinterId) return;

    const subtotal = calculateSubtotal();
    const discount = calculateDiscount();
    const checkoutTotal = calculateCheckoutTotal();

    // 支払い方法の表示名を取得（マスタから）
    const selectedPaymentMethod = paymentMethods.find(m => m.code === paymentMethod);
    const paymentMethodLabel = selectedPaymentMethod?.name || paymentMethod;
    const isCashPayment = selectedPaymentMethod?.type === 'cash';

    const printData = {
      printerId: shop.receiptPrinterId,
      type: 'pos', // POSタイプ
      customerName: customerName || '匿名',
      items: posCart.map(item => ({
        productName: item.menuName,
        quantity: item.quantity,
        price: item.price,
      })),
      subtotal,
      paymentFee: calculatePaymentFee(), paymentFeeRate: getPaymentFeeRate(), creditCardFee: calculatePaymentFee(),
      discount: discount > 0 ? {
        type: discountType,
        value: parseFloat(discountValue),
        amount: discount,
        reason: discountReason || undefined,
      } : undefined,
      total: checkoutTotal,
      paymentMethod: paymentMethodLabel,
      paymentMethodCode: paymentMethod,
      receivedAmount: isCashPayment ? parseFloat(receivedAmount) : checkoutTotal,
      change: isCashPayment ? Math.max(0, parseFloat(receivedAmount) - checkoutTotal) : 0,
      // レシート設定
      receiptSettings: shop.receiptSettings || {},
    };

    // ローカルプリンターサーバーを呼び出し
    const response = await fetch('http://localhost:3100/api/print/receipt', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(printData),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'レシート印刷に失敗しました');
    }
  };

  // 手動レシート印刷（印刷ボタン押下時）
  const handlePrintReceipt = async () => {
    if (!selectedTable || !shop) return;

    try {
      if (!shop.receiptPrinterId) {
        setError('レシートプリンターが設定されていません。プリンター設定ページで設定してください。');
        return;
      }

      // 会計対象の注文を取得
      let ordersToPrint: Order[] = [];
      if (checkoutType === 'group') {
        ordersToPrint = selectedTable.orders;
      } else {
        ordersToPrint = selectedTable.groups
          .filter(g => selectedUsers.has(g.userId))
          .flatMap(g => g.orders);
      }

      await printReceiptAuto(ordersToPrint);

      setSuccess('レシートを印刷しました');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error: any) {
      console.error('レシート印刷エラー:', error);
      setError(`レシート印刷に失敗しました: ${error.message}`);
    }
  };

  const calculateChange = () => {
    if (paymentMethod !== 'cash') return 0;
    const received = parseFloat(receivedAmount) || 0;
    const checkoutTotal = calculateCheckoutTotal();
    return Math.max(0, received - checkoutTotal);
  };

  const formatDateTime = (date: Date) => {
    return new Intl.DateTimeFormat('ja-JP', {
      month: 'numeric',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const calculateItemTotal = (item: OrderItem) => {
    const optionsTotal = item.selectedOptions?.reduce((sum, opt) => sum + opt.price, 0) || 0;
    return ((item.unitPrice || item.price) + optionsTotal) * item.quantity;
  };

  if (loading) {
    return (
      <Container>
        <Typography>読み込み中...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">会計</Typography>
        {checkoutMode === 'mobile_order' && tableOrders.length >= 2 && (
          <Button
            variant={multiSelectMode ? 'contained' : 'outlined'}
            color={multiSelectMode ? 'secondary' : 'primary'}
            onClick={() => {
              if (multiSelectMode) {
                setMultiSelectMode(false);
                setSelectedTableIds(new Set());
              } else {
                setMultiSelectMode(true);
              }
            }}
          >
            {multiSelectMode ? '選択を解除' : 'テーブル統合'}
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* POSモードのUI */}
      {checkoutMode === 'pos' ? (
        <Grid container spacing={3}>
          {/* 商品/メニュー一覧 */}
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 2 }}>
              <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h6">
                  {shop?.mobileOrderEnabled ? '商品選択' : 'メニュー選択'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => navigate('/reservations')}
                  >
                    予約から会計
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => navigate('/customers', { state: { selectMode: 'checkout' } })}
                  >
                    顧客から会計
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    color="secondary"
                    onClick={() => setCustomItemDialogOpen(true)}
                  >
                    自由入力
                  </Button>
                  <TextField
                    label="お客様名（任意）"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    size="small"
                    sx={{ width: 200 }}
                  />
                </Box>
              </Box>

              {/* カテゴリタブと商品一覧（productsがある場合） */}
              {products.length > 0 ? (
                <>
                  {/* カテゴリタブ */}
                  <Tabs
                    value={selectedCategoryId}
                    onChange={(_, newValue) => setSelectedCategoryId(newValue)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
                  >
                    <Tab value="all" label="すべて" />
                    {categories.map((category) => (
                      <Tab key={category.id} value={category.id} label={category.name} />
                    ))}
                  </Tabs>

                  {/* 商品一覧（画像付き） */}
                  <Grid container spacing={2}>
                    {products
                      .filter(product => selectedCategoryId === 'all' || product.categoryId === selectedCategoryId)
                      .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
                      .map((product) => (
                        <Grid item xs={6} sm={4} md={3} key={product.id}>
                          <Card
                            sx={{
                              cursor: 'pointer',
                              '&:hover': { bgcolor: 'action.hover', transform: 'scale(1.02)' },
                              transition: 'transform 0.2s',
                              height: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                            }}
                            onClick={() => handleAddProductToCart(product)}
                          >
                            {/* 商品画像 */}
                            {product.imageUrl ? (
                              <CardMedia
                                component="img"
                                height="120"
                                image={product.imageUrl}
                                alt={product.name}
                                sx={{ objectFit: 'cover' }}
                              />
                            ) : (
                              <Box
                                sx={{
                                  height: 120,
                                  bgcolor: 'grey.200',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <Typography variant="body2" color="text.secondary">
                                  No Image
                                </Typography>
                              </Box>
                            )}
                            <CardContent sx={{ p: 1.5, flexGrow: 1 }}>
                              <Typography variant="body2" noWrap gutterBottom fontWeight="medium">
                                {product.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                {categories.find(c => c.id === product.categoryId)?.name || ''}
                              </Typography>
                              <Typography variant="h6" color="primary" sx={{ mt: 0.5 }}>
                                ¥{product.price.toLocaleString()}
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                  </Grid>
                  {products.filter(p => selectedCategoryId === 'all' || p.categoryId === selectedCategoryId).length === 0 && (
                    <Box sx={{ py: 4, textAlign: 'center' }}>
                      <Typography color="text.secondary">
                        商品がありません
                      </Typography>
                    </Box>
                  )}
                </>
              ) : (
                /* 予約用メニュー一覧（モバイルオーダー無効時） */
                <Grid container spacing={2}>
                  {menus.map((menu) => (
                    <Grid item xs={6} sm={4} md={3} key={menu.id}>
                      <Card
                        sx={{
                          cursor: 'pointer',
                          '&:hover': { bgcolor: 'action.hover' },
                          height: '100%',
                        }}
                        onClick={() => handleAddMenuToCart(menu)}
                      >
                        <CardContent sx={{ p: 1.5 }}>
                          <Typography variant="body2" noWrap gutterBottom>
                            {menu.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {menu.category}
                          </Typography>
                          <Typography variant="h6" color="primary" sx={{ mt: 1 }}>
                            ¥{menu.price.toLocaleString()}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Paper>
          </Grid>

          {/* カート */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, position: 'sticky', top: 16 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ShoppingCartIcon />
                  <Typography variant="h6">カート</Typography>
                </Box>
                {posCart.length > 0 && (
                  <IconButton size="small" onClick={handleClearCart} color="error">
                    <DeleteIcon />
                  </IconButton>
                )}
              </Box>

              <Button
                variant="outlined"
                color="secondary"
                fullWidth
                size="small"
                sx={{ mb: 2 }}
                onClick={() => setCustomItemDialogOpen(true)}
              >
                + 自由入力商品を追加
              </Button>

              {posCart.length === 0 ? (
                <Typography color="text.secondary" textAlign="center" sx={{ py: 4 }}>
                  商品を選択してください
                </Typography>
              ) : (
                <>
                  <List dense sx={{ maxHeight: 400, overflowY: 'auto', mb: 2 }}>
                    {posCart.map((item) => (
                      <ListItem
                        key={item.menuId}
                        sx={{
                          bgcolor: 'grey.50',
                          mb: 1,
                          borderRadius: 1,
                          py: 1,
                        }}
                      >
                        {/* 商品画像 */}
                        <Avatar
                          src={item.imageUrl}
                          variant="rounded"
                          sx={{ width: 48, height: 48, mr: 1.5, bgcolor: 'grey.300' }}
                        >
                          {item.menuName.charAt(0)}
                        </Avatar>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {item.menuName}
                              {item.isCustomItem && (
                                <Chip label="自由" size="small" color="secondary" sx={{ height: 18, fontSize: '0.6rem' }} />
                              )}
                            </Box>
                          }
                          secondary={
                            <>
                              ¥{item.price.toLocaleString()}
                              {item.costInfo && ` (原価: ¥${item.costInfo.cost.toLocaleString()})`}
                            </>
                          }
                          sx={{ mr: 1 }}
                        />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <IconButton
                            size="small"
                            onClick={() => handleUpdateCartQuantity(item.menuId, item.quantity - 1)}
                          >
                            <RemoveIcon fontSize="small" />
                          </IconButton>
                          <Typography sx={{ minWidth: 30, textAlign: 'center' }}>
                            {item.quantity}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => handleUpdateCartQuantity(item.menuId, item.quantity + 1)}
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveFromCart(item.menuId)}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </ListItem>
                    ))}
                  </List>

                  <Divider sx={{ my: 2 }} />

                  <Box sx={{ mb: 2 }}>
                    {/* 時間制の席料。小計に含まれているので、内訳として先に出す。
                        含まれているのに出さないと「何の金額か分からない」になる。 */}
                    {seatCharge.applies && seatCharge.result && (
                      <>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            席料（{seatCharge.result.units}単位・{seatCharge.stayMinutes}分）
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            ¥{seatCharge.result.seatCharge.toLocaleString()}
                          </Typography>
                        </Box>
                        {seatCharge.result.shortStayFee > 0 && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" color="text.secondary">短い滞在の追加</Typography>
                            <Typography variant="body2" color="text.secondary">
                              ¥{seatCharge.result.shortStayFee.toLocaleString()}
                            </Typography>
                          </Box>
                        )}
                        {seatCharge.result.minimumTopUp > 0 && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" color="text.secondary">ミニマムの底上げ</Typography>
                            <Typography variant="body2" color="text.secondary">
                              ¥{seatCharge.result.minimumTopUp.toLocaleString()}
                            </Typography>
                          </Box>
                        )}
                        {seatCharge.result.serviceCharge > 0 && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" color="text.secondary">サービス料</Typography>
                            <Typography variant="body2" color="text.secondary">
                              ¥{seatCharge.result.serviceCharge.toLocaleString()}
                            </Typography>
                          </Box>
                        )}
                      </>
                    )}
                    {/* 席料が出せないときは、黙って0円にせず理由を出す。
                        深夜の客だけ席料が付かないまま会計が終わる事故を防ぐ。 */}
                    {seatCharge.applies && seatCharge.error && (
                      <Alert severity="warning" sx={{ mb: 1 }}>
                        席料を計算できません: {seatCharge.error}<br />
                        設定 → 時間制の席料 で、この時間帯の単価を入れてください。
                      </Alert>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography>小計</Typography>
                      <Typography>¥{calculateSubtotal().toLocaleString()}</Typography>
                    </Box>
                    {calculateTax() > 0 && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">消費税 ({taxRatePercentLabel(shop)})</Typography>
                        <Typography variant="body2" color="text.secondary">¥{calculateTax().toLocaleString()}</Typography>
                      </Box>
                    )}
                    {calculatePaymentFee() > 0 && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">決済手数料 ({(getPaymentFeeRate() * 100).toFixed(0)}%)</Typography>
                        <Typography variant="body2" color="text.secondary">¥{calculatePaymentFee().toLocaleString()}</Typography>
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, fontSize: '1.2rem', fontWeight: 'bold' }}>
                      <Typography variant="h6">合計</Typography>
                      <Typography variant="h6" color="primary">
                        ¥{calculateCheckoutTotal().toLocaleString()}
                      </Typography>
                    </Box>
                  </Box>

                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    size="large"
                    onClick={handleOpenPOSCheckout}
                    startIcon={<PointOfSaleIcon />}
                  >
                    会計する
                  </Button>
                </>
              )}
            </Paper>
          </Grid>
        </Grid>
      ) : checkoutMode === 'mobile_order' && tableOrders.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">
            会計待ちのテーブルはありません
          </Typography>
        </Paper>
      ) : (
        <>
        <Grid container spacing={3}>
          {tableOrders.map((table) => (
            <Grid item xs={12} sm={6} md={4} key={table.tableId}>
              <Card
                sx={{
                  ...(multiSelectMode && selectedTableIds.has(table.tableId) && {
                    border: 2,
                    borderColor: 'primary.main',
                  }),
                  ...(multiSelectMode && {
                    cursor: 'pointer',
                  }),
                }}
                onClick={multiSelectMode ? () => handleToggleTableSelection(table.tableId) : undefined}
              >
                <CardContent>
                  {multiSelectMode && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Checkbox
                        checked={selectedTableIds.has(table.tableId)}
                        onChange={() => handleToggleTableSelection(table.tableId)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <Typography variant="caption" color="text.secondary">
                        統合会計に含める
                      </Typography>
                    </Box>
                  )}

                  <Typography variant="h5" gutterBottom>
                    {table.tableNumber}
                  </Typography>

                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    注文数: {table.orders.length}件
                  </Typography>

                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    商品数: {table.orders.reduce((sum, order) =>
                      sum + order.items.reduce((s, item) => s + item.quantity, 0), 0
                    )}点
                  </Typography>

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="h6" color="primary" gutterBottom>
                    合計: ¥{table.total.toLocaleString()}
                  </Typography>

                  {!multiSelectMode && (
                    <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        startIcon={<ReceiptIcon />}
                        onClick={() => handleOpenCheckout(table)}
                      >
                        会計する
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<SwapHorizIcon />}
                        onClick={() => setTransferDialog({ open: true, table })}
                        sx={{ minWidth: 'auto', whiteSpace: 'nowrap' }}
                      >
                        転卓
                      </Button>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* 統合会計フローティングバー */}
        {multiSelectMode && selectedTableIds.size >= 2 && (
          <Paper
            elevation={6}
            sx={{
              position: 'fixed',
              bottom: 24,
              left: '50%',
              transform: 'translateX(-50%)',
              px: 4,
              py: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              borderRadius: 3,
              zIndex: 1200,
            }}
          >
            <Typography variant="body1" fontWeight="bold">
              {selectedTableIds.size}テーブル選択中
              （合計: ¥{tableOrders
                .filter(t => selectedTableIds.has(t.tableId))
                .reduce((sum, t) => sum + t.total, 0)
                .toLocaleString()}）
            </Typography>
            <Button
              variant="contained"
              color="primary"
              size="large"
              startIcon={<ReceiptIcon />}
              onClick={handleOpenMergedCheckout}
            >
              統合して会計
            </Button>
          </Paper>
        )}
        </>
      )}

      {/* 会計ダイアログ */}
      <Dialog
        open={openCheckoutDialog}
        onClose={() => { setOpenCheckoutDialog(false); setMergedSourceTableIds([]); }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          会計 - {
            checkoutMode === 'pos' ? (customerName || '匿名') :
            reservationCheckout ? reservationCheckout.userName + '様' :
            selectedTable?.tableNumber
          }
          {mergedSourceTableIds.length > 1 && (
            <Chip label="統合会計" size="small" color="secondary" sx={{ ml: 1 }} />
          )}
        </DialogTitle>
        <DialogContent>
          {/* POSモードの会計 */}
          {checkoutMode === 'pos' && (
            <Box>
              {/* 商品一覧 */}
              <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                  購入商品
                </Typography>
                <List dense>
                  {posCart.map((item) => (
                    <ListItem key={item.menuId} sx={{ px: 0 }}>
                      <ListItemText
                        primary={`${item.menuName} × ${item.quantity}`}
                        secondary={item.category}
                      />
                      <Typography>
                        ¥{(item.price * item.quantity).toLocaleString()}
                      </Typography>
                    </ListItem>
                  ))}
                </List>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold">小計</Typography>
                  <Typography variant="subtitle1" fontWeight="bold">
                    ¥{calculateSubtotal().toLocaleString()}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />
            </Box>
          )}

          {/* 予約ベースの会計 */}
          {reservationCheckout && (
            <Box>
              {/* 予約情報の表示 */}
              <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                  予約情報
                </Typography>
                <List dense>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText primary="お客様名" secondary={reservationCheckout.userName} />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText
                      primary="予約日時"
                      secondary={`${reservationCheckout.reservationDate?.toDate?.().toLocaleDateString('ja-JP') || ''} ${reservationCheckout.startTime}`}
                    />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText primary="メニュー" secondary={reservationCheckout.menuName} />
                  </ListItem>
                  {reservationCheckout.staffName && (
                    <ListItem sx={{ px: 0 }}>
                      <ListItemText primary="スタッフ" secondary={reservationCheckout.staffName} />
                    </ListItem>
                  )}
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText
                      primary="料金"
                      secondary={`¥${reservationCheckout.totalPrice.toLocaleString()}`}
                      secondaryTypographyProps={{ sx: { fontWeight: 'bold', fontSize: '1.1rem' } }}
                    />
                  </ListItem>
                </List>
              </Box>

              <Divider sx={{ my: 2 }} />
            </Box>
          )}

          {/* モバイルオーダーベースの会計 */}
          {selectedTable && !reservationCheckout && (
            <Box>
              {/* 会計タイプ選択 */}
              <FormControl component="fieldset" sx={{ mb: 3 }}>
                <FormLabel component="legend">会計方法</FormLabel>
                <RadioGroup
                  value={checkoutType}
                  onChange={(e) => {
                    setCheckoutType(e.target.value as 'group' | 'individual');
                    // 会計タイプ変更時に受け取り金額をリセット
                    if (e.target.value === 'group') {
                      setReceivedAmount(selectedTable.total.toString());
                      setSelectedUsers(new Set());
                    } else {
                      setReceivedAmount('');
        setAllowanceInputs([]);
                    }
                  }}
                >
                  <FormControlLabel
                    value="group"
                    control={<Radio />}
                    label={`まとめて会計（全員 - ¥${selectedTable.total.toLocaleString()}）`}
                  />
                  <FormControlLabel
                    value="individual"
                    control={<Radio />}
                    label="個別会計（選択した人のみ）"
                  />
                </RadioGroup>
              </FormControl>

              {/* 個別会計の場合はユーザー選択 */}
              {checkoutType === 'individual' && (
                <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                    会計する人を選択
                  </Typography>
                  <List>
                    {selectedTable.groups.map((group) => (
                      <ListItem key={group.userId} dense sx={{ px: 0 }}>
                        <Checkbox
                          checked={selectedUsers.has(group.userId)}
                          onChange={() => {
                            handleUserSelection(group.userId);
                            // 選択変更時に受け取り金額を更新
                            const newSelected = new Set(selectedUsers);
                            if (newSelected.has(group.userId)) {
                              newSelected.delete(group.userId);
                            } else {
                              newSelected.add(group.userId);
                            }
                            const newTotal = selectedTable.groups
                              .filter(g => newSelected.has(g.userId))
                              .reduce((sum, g) => sum + g.total, 0);
                            setReceivedAmount(newTotal.toString());
                          }}
                        />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                          <PersonIcon fontSize="small" />
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2">{group.userName}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {group.orders.length}件の注文
                            </Typography>
                          </Box>
                          <Typography variant="body2" fontWeight="bold">
                            ¥{group.total.toLocaleString()}
                          </Typography>
                        </Box>
                      </ListItem>
                    ))}
                  </List>
                  {selectedUsers.size > 0 && (
                    <Alert severity="info" sx={{ mt: 1 }}>
                      選択中: {selectedUsers.size}名 - ¥{calculateCheckoutTotal().toLocaleString()}
                    </Alert>
                  )}
                </Box>
              )}

              <Divider sx={{ my: 2 }} />
            </Box>
          )}

          {/* 注文内容（モバイルオーダーのみ） */}
          {selectedTable && !reservationCheckout && (
            <>
              <Typography variant="h6" gutterBottom>
                注文内容
              </Typography>
              <Box sx={{ maxHeight: 200, overflowY: 'auto', mb: 2 }}>
                <List dense>
                  {(checkoutType === 'group'
                    ? selectedTable.orders
                    : selectedTable.groups
                        .filter(g => selectedUsers.has(g.userId))
                        .flatMap(g => g.orders)
                  ).map((order) => (
                    <Box key={order.id} sx={{ mb: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        {order.orderNumber}
                      </Typography>
                      {order.items.map((item, index) => {
                        const isCancelled = (item as any).itemStatus === 'cancelled';
                        return (
                          <ListItem key={index} sx={{ py: 0, px: 0, opacity: isCancelled ? 0.4 : 1 }}>
                            <ListItemText
                              primary={
                                <Typography variant="body2" sx={{
                                  textDecoration: isCancelled ? 'line-through' : 'none',
                                }}>
                                  {getLocalizedProductName(item, language)} × {item.quantity}
                                  {isCancelled && ' (キャンセル)'}
                                </Typography>
                              }
                              secondary={
                                item.selectedOptions && item.selectedOptions.length > 0
                                  ? item.selectedOptions.map(opt => opt.choiceName).join(', ')
                                  : null
                              }
                            />
                            {!isCancelled && (
                              <Typography variant="body2">
                                ¥{calculateItemTotal(item).toLocaleString()}
                              </Typography>
                            )}
                          </ListItem>
                        );
                      })}
                    </Box>
                  ))}
                </List>
              </Box>

              <Divider sx={{ my: 2 }} />
            </>
          )}

          {/* プラン注文内訳（ゲスト別） */}
          {activePlanOrders.length > 0 && (
            <>
              <Typography variant="h6" gutterBottom>
                プラン注文内訳
              </Typography>
              <Box sx={{ mb: 2 }}>
                {activePlanOrders.map((plan, idx) => {
                  const extensionPrice = computeEffectiveExtensionPrice(plan as any);
                  const extensionCount = plan.extensions?.length || 0;
                  const subtotal = (plan.planPrice || 0) + extensionPrice + (plan.nominationFee || 0);

                  return (
                    <Paper key={plan.id || idx} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {plan.guestLabel || `ゲスト${(plan.guestIndex ?? idx) + 1}`}
                        </Typography>
                        <Typography variant="subtitle2" fontWeight="bold">
                          ¥{subtotal.toLocaleString()}
                        </Typography>
                      </Box>

                      {/* プラン名 + 料金 */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', pl: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          {plan.planName}
                        </Typography>
                        <Typography variant="body2">
                          ¥{(plan.planPrice || 0).toLocaleString()}
                        </Typography>
                      </Box>

                      {/* 指名スタッフ + 指名料 */}
                      {plan.nominatedStaffName && (plan.nominationFee || 0) > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', pl: 1, alignItems: 'center' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <StarIcon sx={{ fontSize: 14, color: 'warning.main' }} />
                            <Typography variant="body2" color="text.secondary">
                              指名: {plan.nominatedStaffName}
                              {plan.nominationType === 'honshimei' && ' (本指名)'}
                              {plan.nominationType === 'banai' && ' (場内指名)'}
                            </Typography>
                          </Box>
                          <Typography variant="body2">
                            ¥{(plan.nominationFee || 0).toLocaleString()}
                          </Typography>
                        </Box>
                      )}

                      {/* 延長回数 + 延長料金 */}
                      {extensionCount > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', pl: 1, alignItems: 'center' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <TimerIcon sx={{ fontSize: 14, color: 'info.main' }} />
                            <Typography variant="body2" color="text.secondary">
                              延長 ×{plan.checkoutAdjustment
                                ? plan.checkoutAdjustment.adjustedExtensionCount
                                : extensionCount}
                              {plan.checkoutAdjustment && (
                                <Typography component="span" variant="caption" color="warning.main" sx={{ ml: 0.5 }}>
                                  (調整済)
                                </Typography>
                              )}
                            </Typography>
                          </Box>
                          <Typography variant="body2">
                            ¥{extensionPrice.toLocaleString()}
                          </Typography>
                        </Box>
                      )}
                    </Paper>
                  );
                })}

                {/* プラン合計 */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, px: 1 }}>
                  <Typography variant="subtitle2" fontWeight="bold">プラン合計</Typography>
                  <Typography variant="subtitle2" fontWeight="bold">
                    ¥{activePlanOrders.reduce((sum, p) => {
                      const extPrice = computeEffectiveExtensionPrice(p as any);
                      return sum + (p.planPrice || 0) + extPrice + (p.nominationFee || 0);
                    }, 0).toLocaleString()}
                  </Typography>
                </Box>
              </Box>
              <Divider sx={{ my: 2 }} />
            </>
          )}

              {/* 割引・クーポン・加算設定 */}
              <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                  割引・クーポン・加算
                </Typography>
                <FormControl component="fieldset" sx={{ mb: 2 }}>
                  <RadioGroup
                    row
                    value={discountType}
                    onChange={(e) => {
                      setDiscountType(e.target.value as any);
                      setDiscountValue('');
                    }}
                  >
                    <FormControlLabel
                      value="none"
                      control={<Radio size="small" />}
                      label="なし"
                    />
                    <FormControlLabel
                      value="amount"
                      control={<Radio size="small" />}
                      label="固定額割引"
                    />
                    <FormControlLabel
                      value="percentage"
                      control={<Radio size="small" />}
                      label="％割引"
                    />
                    <FormControlLabel
                      value="surcharge"
                      control={<Radio size="small" />}
                      label="加算"
                    />
                  </RadioGroup>
                </FormControl>

                {discountType !== 'none' && (
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <TextField
                      label={
                        discountType === 'amount' ? '割引額（円）' :
                        discountType === 'percentage' ? '割引率（%）' :
                        '加算額（円）'
                      }
                      type="number"
                      value={discountValue}
                      onChange={(e) => setDiscountValue(e.target.value)}
                      size="small"
                      sx={{ flex: 1 }}
                      inputProps={{
                        min: 0,
                        max: discountType === 'percentage' ? 100 : undefined,
                      }}
                    />
                    <TextField
                      label={discountType === 'surcharge' ? '加算理由' : 'クーポンコード/理由'}
                      value={discountReason}
                      onChange={(e) => setDiscountReason(e.target.value)}
                      size="small"
                      sx={{ flex: 1 }}
                      placeholder={discountType === 'surcharge' ? '例: サービス料' : '例: 誕生日クーポン'}
                    />
                  </Box>
                )}

                {calculateDiscount() > 0 && (
                  <Alert severity="success" sx={{ mt: 1 }}>
                    割引額: -¥{calculateDiscount().toLocaleString()}
                    {discountReason && ` (${discountReason})`}
                  </Alert>
                )}
                {calculateDiscount() < 0 && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    加算額: +¥{Math.abs(calculateDiscount()).toLocaleString()}
                    {discountReason && ` (${discountReason})`}
                  </Alert>
                )}
              </Box>

              {/* 販売スタッフ選択（原価・バック設定またはインセンティブ設定が有効な場合のみ） */}
              {(shop?.costBackSettings?.enabled || shop?.incentiveSettings?.enabled) && staffList.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ mb: 3, p: 2, bgcolor: 'blue.50', borderRadius: 1, border: '1px solid', borderColor: 'primary.light' }}>
                    <Typography variant="subtitle2" gutterBottom fontWeight="bold" color="primary">
                      {shop?.incentiveSettings?.enabled ? '担当（インセンティブ対象）' : '販売スタッフ（バック対象）'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                      {shop?.incentiveSettings?.enabled
                        ? 'この売上の担当を選択してください。お店と複数スタッフで按分できます。'
                        : 'この売上に対してバックを付与するスタッフを選択してください。複数選択の場合は分配率を設定できます。'}
                    </Typography>

                    {/* スタッフ選択チップ */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                      {/* インセンティブ対応店舗の場合「お店」を追加 */}
                      {shop?.incentiveSettings?.enabled && (() => {
                        const isShopSelected = selectedSalesStaff.some(s => s.staffId === '__shop__');
                        return (
                          <Chip
                            key="__shop__"
                            avatar={<Avatar sx={{ bgcolor: 'warning.main' }}>店</Avatar>}
                            label="お店"
                            onClick={() => {
                              if (isShopSelected) {
                                setSelectedSalesStaff(prev => prev.filter(s => s.staffId !== '__shop__'));
                              } else {
                                const newStaff: SelectedSalesStaff = {
                                  staffId: '__shop__',
                                  staffName: '店',
                                  shareRate: 1,
                                  allocationType: 'percent',
                                  allocValue: 100,
                                  resolvedAmount: 0,
                                };
                                setSelectedSalesStaff(prev => {
                                  const updated = [...prev, newStaff];
                                  const equalPercent = Math.floor(100 / updated.length);
                                  return updated.map((s, i) => ({
                                    ...s,
                                    allocValue: i === updated.length - 1 ? 100 - equalPercent * (updated.length - 1) : equalPercent,
                                    shareRate: 1 / updated.length,
                                  }));
                                });
                              }
                            }}
                            color={isShopSelected ? 'warning' : 'default'}
                            variant={isShopSelected ? 'filled' : 'outlined'}
                            sx={{ cursor: 'pointer' }}
                          />
                        );
                      })()}
                      {staffList.map((staff) => {
                        const isSelected = selectedSalesStaff.some(s => s.staffId === staff.id);
                        return (
                          <Chip
                            key={staff.id}
                            avatar={staff.photo ? <Avatar src={staff.photo} /> : <Avatar>{staff.name[0]}</Avatar>}
                            label={staff.name}
                            onClick={() => {
                              if (isSelected) {
                                setSelectedSalesStaff(prev => prev.filter(s => s.staffId !== staff.id));
                              } else {
                                // 新しいスタッフを追加し、分配率を均等に再計算
                                const newStaff: SelectedSalesStaff = {
                                  staffId: staff.id,
                                  staffName: staff.name,
                                  shareRate: 1,
                                  allocationType: 'percent',
                                  allocValue: 100,
                                  resolvedAmount: 0,
                                };
                                setSelectedSalesStaff(prev => {
                                  const updated = [...prev, newStaff];
                                  const equalPercent = Math.floor(100 / updated.length);
                                  return updated.map((s, i) => ({
                                    ...s,
                                    allocValue: i === updated.length - 1 ? 100 - equalPercent * (updated.length - 1) : equalPercent,
                                    shareRate: 1 / updated.length,
                                  }));
                                });
                              }
                            }}
                            color={isSelected ? 'primary' : 'default'}
                            variant={isSelected ? 'filled' : 'outlined'}
                            sx={{ cursor: 'pointer' }}
                          />
                        );
                      })}
                    </Box>

                    {/* 選択済みスタッフの配分設定 */}
                    {selectedSalesStaff.length > 0 && (() => {
                      const incentiveInfo = checkoutMode === 'pos'
                        ? calculateIncentiveBase([{ items: posCart }])
                        : calculateIncentiveBase(selectedTable?.orders || []);
                      const netSales = incentiveInfo.incentiveBase;
                      const resolved = resolveAllocations(selectedSalesStaff, netSales);
                      const allocTotal = resolved.reduce((sum, s) => sum + s.resolvedAmount, 0);
                      // 入力の妥当性を確認する。
                      // 実データでは % 欄に金額（1050 / 3050）が入っていて、
                      // 合計 4100% のまま比率として通り、意図と違う額が確定していた。
                      const allocCheck = validateAllocations(
                        selectedSalesStaff.map(st => ({
                          staffId: st.staffId,
                          staffName: st.staffName,
                          allocationType: st.allocationType,
                          allocValue: st.allocValue,
                        })),
                        netSales
                      );

                      return (
                        <Box sx={{ mt: 2, p: 2, bgcolor: 'white', borderRadius: 1 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                            配分対象額: ¥{netSales.toLocaleString()}（小計 - 原価）
                          </Typography>
                          {allocCheck.errors.length > 0 && (
                            <Alert severity="error" sx={{ mb: 1, whiteSpace: 'pre-line' }}>
                              {allocCheck.errors.join('\n')}
                            </Alert>
                          )}
                          {allocCheck.warnings.length > 0 && (
                            <Alert severity="warning" sx={{ mb: 1, whiteSpace: 'pre-line' }}>
                              {allocCheck.warnings.join('\n')}
                            </Alert>
                          )}
                          {selectedSalesStaff.map((staff, index) => {
                            const r = resolved.find(rs => rs.staffId === staff.staffId);
                            return (
                              <Box
                                key={staff.staffId}
                                sx={{
                                  display: 'grid',
                                  gridTemplateColumns: { xs: '1fr', sm: '1fr auto auto auto' },
                                  alignItems: 'center',
                                  gap: 1,
                                  mb: 1,
                                  pb: { xs: 1, sm: 0 },
                                  borderBottom: { xs: '1px dashed #eee', sm: 'none' },
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 500,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                  title={staff.staffName}
                                >
                                  {staff.staffName}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
                                  <TextField
                                    select
                                    size="small"
                                    value={staff.allocationType}
                                    onChange={(e) => {
                                      setSelectedSalesStaff(prev =>
                                        prev.map((s, i) => i === index ? {
                                          ...s,
                                          allocationType: e.target.value as 'percent' | 'amount',
                                          allocValue: e.target.value === 'percent' ? Math.round(s.shareRate * 100) : s.resolvedAmount || 0,
                                        } : s)
                                      );
                                    }}
                                    sx={{ width: 76 }}
                                  >
                                    <MenuItem value="percent">%</MenuItem>
                                    <MenuItem value="amount">円</MenuItem>
                                  </TextField>
                                  <TextField
                                    type="number"
                                    size="small"
                                    value={staff.allocValue}
                                    onChange={(e) => {
                                      const val = Math.max(0, Number(e.target.value));
                                      setSelectedSalesStaff(prev =>
                                        prev.map((s, i) => i === index ? { ...s, allocValue: val } : s)
                                      );
                                    }}
                                    sx={{ width: 100 }}
                                    inputProps={{ min: 0 }}
                                  />
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ minWidth: 80, textAlign: 'right', whiteSpace: 'nowrap' }}
                                  >
                                    ¥{(r?.resolvedAmount || 0).toLocaleString()}
                                  </Typography>
                                </Box>
                              </Box>
                            );
                          })}
                          {allocTotal !== netSales && netSales > 0 && (
                            <Alert severity="warning" sx={{ mt: 1 }}>
                              配分合計 ¥{allocTotal.toLocaleString()} / 対象額 ¥{netSales.toLocaleString()}（差額は「店」に加算されます）
                            </Alert>
                          )}
                          {allocTotal === netSales && netSales > 0 && (
                            <Alert severity="success" sx={{ mt: 1 }}>
                              配分OK（¥{netSales.toLocaleString()}）
                            </Alert>
                          )}
                        </Box>
                      );
                    })()}

                    {selectedSalesStaff.length === 0 && (
                      <Typography variant="caption" color="text.secondary">
                        スタッフ未選択 → 全額「店」の売上になります
                      </Typography>
                    )}
                  </Box>
                </>
              )}

              <Divider sx={{ my: 2 }} />

              {/* 金額サマリー */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body1">小計</Typography>
                  <Typography variant="body1">
                    ¥{calculateSubtotal().toLocaleString()}
                  </Typography>
                </Box>
                {calculateDiscount() > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body1" color="error">
                      割引
                      {discountType === 'percentage' && ` (${discountValue}%)`}
                    </Typography>
                    <Typography variant="body1" color="error">
                      -¥{calculateDiscount().toLocaleString()}
                    </Typography>
                  </Box>
                )}
                {calculateDiscount() < 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body1" color="info.main">
                      加算
                    </Typography>
                    <Typography variant="body1" color="info.main">
                      +¥{Math.abs(calculateDiscount()).toLocaleString()}
                    </Typography>
                  </Box>
                )}
                {calculateServiceChargeTotal() > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      (内 サービス料)
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      ¥{calculateServiceChargeTotal().toLocaleString()}
                    </Typography>
                  </Box>
                )}
                {calculateTax() > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body1">
                      消費税 ({taxRatePercentLabel(shop)})
                    </Typography>
                    <Typography variant="body1">
                      ¥{calculateTax().toLocaleString()}
                    </Typography>
                  </Box>
                )}
                {calculatePaymentFee() > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body1">
                      決済手数料 ({(getPaymentFeeRate() * 100).toFixed(0)}%)
                    </Typography>
                    <Typography variant="body1">
                      ¥{calculatePaymentFee().toLocaleString()}
                    </Typography>
                  </Box>
                )}
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="h6">合計</Typography>
                  <Typography variant="h6" color="primary" fontWeight="bold">
                    ¥{calculateCheckoutTotal().toLocaleString()}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              <FormControl component="fieldset" sx={{ mb: 3 }}>
                {(() => {
                  const gate = evaluatePlan((shop as any)?.planTier, slipUsage);
                  const check = canCreateSlip(gate);
                  if (!check.message) return null;
                  return (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      <AlertTitle>{PLAN_LABELS[gate.tier]}の伝票数の上限を超えています</AlertTitle>
                      {check.message}
                    </Alert>
                  );
                })()}

                {/* 手当（同伴・指名・ボトルバック等）。
                    以前は夜職Pro側にしか無く、会計にも給与にも入らなかった。 */}
                {allowanceTypes.length > 0 && (
                  <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      手当（指名・同伴・ボトルバック）
                    </Typography>
                    <Stack spacing={1} sx={{ mb: 1 }}>
                      {allowanceTypes.map((t) => {
                        const cur = allowanceInputs.find((a) => a.allowanceTypeId === t.id) ?? null;
                        return (
                          <Box key={t.id} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 90px 1fr' }, gap: 1, alignItems: 'center' }}>
                            <Typography variant="body2">
                              {t.name}
                              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                {t.calcType === 'percent'
                                  ? `小計の${Math.round(t.amount * 1000) / 10}%`
                                  : `¥${t.amount.toLocaleString()}`}
                                ／バック{Math.round(t.backRate * 100)}%
                              </Typography>
                            </Typography>
                            <TextField
                              type="number" size="small" label="数"
                              value={cur?.quantity ?? 0}
                              onChange={(e) => {
                                const q = Math.max(0, Number(e.target.value));
                                setAllowanceInputs((prev) => {
                                  const next = prev.filter((a) => a.allowanceTypeId !== t.id);
                                  if (q > 0) next.push({ allowanceTypeId: t.id, quantity: q, staffId: cur?.staffId, staffName: cur?.staffName });
                                  return next;
                                });
                              }}
                            />
                            <TextField
                              select size="small" label="担当"
                              value={cur?.staffId ?? ''}
                              disabled={!cur || cur.quantity <= 0}
                              onChange={(e) => {
                                const sid = e.target.value;
                                const st = staffList.find((x) => x.id === sid);
                                setAllowanceInputs((prev) => prev.map((a) =>
                                  a.allowanceTypeId === t.id
                                    ? { ...a, staffId: sid || undefined, staffName: st?.name }
                                    : a));
                              }}
                            >
                              <MenuItem value="">担当なし（店）</MenuItem>
                              {staffList.map((st) => (
                                <MenuItem key={st.id} value={st.id}>{st.name}</MenuItem>
                              ))}
                            </TextField>
                          </Box>
                        );
                      })}
                    </Stack>
                    {allowanceInputs.length > 0 && (() => {
                      const applied = resolveAllowances(allowanceTypes, allowanceInputs, calculateSubtotal());
                      const sum = summarizeAllowances(applied);
                      return (
                        <Alert severity="info" sx={{ mt: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            手当 合計 ¥{sum.chargeTotal.toLocaleString()}（うちバック ¥{sum.backTotal.toLocaleString()}）
                          </Typography>
                          {applied.map((a, i) => (
                            <Typography key={i} variant="caption" display="block" color="text.secondary">
                              {describeAllowance(a)}
                            </Typography>
                          ))}
                          {sum.unassignedBack > 0 && (
                            <Typography variant="caption" color="warning.main" display="block">
                              担当が付いていない手当のバック ¥{sum.unassignedBack.toLocaleString()} は店の取り分になります
                            </Typography>
                          )}
                        </Alert>
                      );
                    })()}
                  </Box>
                )}

                <FormLabel component="legend">支払い方法</FormLabel>
                <RadioGroup
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                >
                  {paymentMethods.map((method) => (
                    <FormControlLabel
                      key={method.id}
                      value={method.code}
                      control={<Radio />}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {method.type === 'cash' && <ReceiptIcon fontSize="small" />}
                          {method.type === 'card' && <CreditCardIcon fontSize="small" />}
                          {method.type === 'transfer' && <AccountBalanceIcon fontSize="small" />}
                          {method.type === 'qr' && <PointOfSaleIcon fontSize="small" />}
                          {method.type === 'receivable' && <AccountBalanceIcon fontSize="small" />}
                          {method.type === 'other' && <ReceiptIcon fontSize="small" />}
                          {method.name}
                        </Box>
                      }
                    />
                  ))}
                </RadioGroup>
              </FormControl>

              {/* 現金の場合のみお預かり金額入力 */}
              {paymentMethods.find(m => m.code === paymentMethod)?.type === 'cash' && (
                <Box sx={{ mb: 3 }}>
                  <TextField
                    label="お預かり金額"
                    type="number"
                    value={receivedAmount}
                    onChange={(e) => setReceivedAmount(e.target.value)}
                    fullWidth
                    sx={{ mb: 2 }}
                  />
                  {parseFloat(receivedAmount) >= calculateCheckoutTotal() && receivedAmount !== '' && (
                    <Alert severity="info">
                      お釣り: ¥{calculateChange().toLocaleString()}
                    </Alert>
                  )}
                  {parseFloat(receivedAmount) < calculateCheckoutTotal() && receivedAmount !== '' && (
                    <Alert severity="warning">
                      金額が不足しています
                    </Alert>
                  )}
                </Box>
              )}

              {/* ツケ（掛け売り）の場合。相手と回収責任者が決まらないと債権にならない */}
              {paymentMethods.find(m => m.code === paymentMethod)?.type === 'receivable' && (
                <Box sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'warning.light', borderRadius: 1 }}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    ツケの内容
                  </Typography>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    ¥{calculateCheckoutTotal().toLocaleString()} を未収金として計上します。入金は未収金台帳から記録してください。
                  </Alert>
                  <TextField
                    label="お客様名（必須）"
                    value={receivableCustomerName}
                    onChange={(e) => setReceivableCustomerName(e.target.value)}
                    fullWidth
                    required
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    label="電話番号"
                    value={receivablePhone}
                    onChange={(e) => setReceivablePhone(e.target.value)}
                    fullWidth
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    select
                    label="回収責任者"
                    value={receivableStaffId}
                    onChange={(e) => setReceivableStaffId(e.target.value)}
                    fullWidth
                    helperText="担当キャストを紐づけると回収率が見えます"
                    sx={{ mb: 2 }}
                  >
                    <MenuItem value="">指定しない</MenuItem>
                    {staffList.map((st) => (
                      <MenuItem key={st.id} value={st.id}>{st.name}</MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    label="支払期日"
                    type="date"
                    value={receivableDueDate}
                    onChange={(e) => setReceivableDueDate(e.target.value)}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>
              )}

          {/* レシート発行オプション */}
          <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom fontWeight="bold">
              レシート発行
            </Typography>

            {/* 紙レシート発行 */}
            {shop?.receiptPrinterId && receiptPrinter && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={printPaperReceipt}
                    onChange={(e) => setPrintPaperReceipt(e.target.checked)}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PrintIcon fontSize="small" />
                    <span>紙レシートを印刷</span>
                  </Box>
                }
              />
            )}

            {/* デジタルレシート送信 */}
            {digitalReceiptRecipients.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={sendDigitalReceiptEnabled}
                      onChange={(e) => {
                        setSendDigitalReceiptEnabled(e.target.checked);
                        if (!e.target.checked) {
                          setSelectedDigitalRecipient(null);
                        } else if (digitalReceiptRecipients.length === 1) {
                          setSelectedDigitalRecipient(digitalReceiptRecipients[0].id);
                        }
                      }}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <SendIcon fontSize="small" />
                      <span>デジタルレシートを送信</span>
                    </Box>
                  }
                />

                {/* 送信先選択（複数名の場合） */}
                {sendDigitalReceiptEnabled && digitalReceiptRecipients.length > 1 && (
                  <Box sx={{ ml: 4, mt: 1, p: 1.5, bgcolor: 'white', borderRadius: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      送信先を選択（1件のみ）
                    </Typography>
                    <RadioGroup
                      value={selectedDigitalRecipient || ''}
                      onChange={(e) => setSelectedDigitalRecipient(e.target.value)}
                    >
                      {digitalReceiptRecipients.map((recipient) => (
                        <FormControlLabel
                          key={recipient.id}
                          value={recipient.id}
                          control={<Radio size="small" />}
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {recipient.type === 'line' ? (
                                <Chip
                                  size="small"
                                  label="LINE"
                                  color="success"
                                  sx={{ height: 20, fontSize: '0.7rem' }}
                                />
                              ) : (
                                <Chip
                                  size="small"
                                  label="SMS"
                                  color="info"
                                  icon={<SmartphoneIcon sx={{ fontSize: '0.9rem !important' }} />}
                                  sx={{ height: 20, fontSize: '0.7rem' }}
                                />
                              )}
                              <Typography variant="body2">{recipient.name}</Typography>
                            </Box>
                          }
                        />
                      ))}
                    </RadioGroup>
                  </Box>
                )}

                {/* 送信先が1件の場合の表示 */}
                {sendDigitalReceiptEnabled && digitalReceiptRecipients.length === 1 && (
                  <Box sx={{ ml: 4, mt: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      送信先: {digitalReceiptRecipients[0].type === 'line' ? 'LINE' : 'SMS'} - {digitalReceiptRecipients[0].name}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {/* レシート発行先がない場合 */}
            {!shop?.receiptPrinterId && digitalReceiptRecipients.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                レシート発行オプションはありません
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1, px: 2, py: 1.5 }}>
          {checkoutMode === 'mobile_order' && !reservationCheckout && (
            <Button
              onClick={handlePrintReceipt}
              startIcon={<PrintIcon />}
              disabled={processing || checkoutType === 'individual' && selectedUsers.size === 0}
              sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              レシート印刷のみ
            </Button>
          )}
          <Box sx={{ flex: 1 }} />
          <Button
            onClick={() => { setOpenCheckoutDialog(false); setMergedSourceTableIds([]); }}
            disabled={processing}
            sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            キャンセル
          </Button>
          <Button
            onClick={handleCheckout}
            variant="contained"
            color="primary"
            startIcon={<CheckCircleIcon />}
            disabled={
              processing ||
              (checkoutMode === 'mobile_order' && !reservationCheckout && checkoutType === 'individual' && selectedUsers.size === 0) ||
              (paymentMethod === 'cash' &&
                (parseFloat(receivedAmount) < calculateCheckoutTotal() || receivedAmount === ''))
            }
            sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            {processing ? '処理中...' : '会計完了'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 転卓ダイアログ */}
      <Dialog
        open={transferDialog.open}
        onClose={() => setTransferDialog({ open: false, table: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          転卓 - {transferDialog.table?.tableNumber}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            移動先のテーブルを選択してください
          </Typography>
          {(() => {
            const currentTableId = transferDialog.table?.tableId;
            const availableForTransfer = allTables.filter(t =>
              t.id !== currentTableId &&
              t.status === 'available'
            );

            if (availableForTransfer.length === 0) {
              return (
                <Alert severity="warning">
                  空きテーブルがありません
                </Alert>
              );
            }

            return (
              <Grid container spacing={2}>
                {availableForTransfer.map((t: any) => (
                  <Grid item xs={4} sm={3} key={t.id}>
                    <Card
                      sx={{
                        cursor: transferring ? 'not-allowed' : 'pointer',
                        textAlign: 'center',
                        border: '2px solid',
                        borderColor: 'success.main',
                        '&:hover': { bgcolor: transferring ? undefined : 'success.light', color: 'white' },
                      }}
                      onClick={() => !transferring && handleTransferTable(t)}
                    >
                      <CardContent sx={{ py: 2, px: 1, '&:last-child': { pb: 2 } }}>
                        <Typography variant="h6" fontWeight="bold">
                          {t.tableNumber}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t.capacity}席
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTransferDialog({ open: false, table: null })}>
            キャンセル
          </Button>
        </DialogActions>
      </Dialog>

      {/* 自由入力商品ダイアログ */}
      <Dialog
        open={customItemDialogOpen}
        onClose={() => setCustomItemDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>自由入力商品</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="商品名"
              fullWidth
              value={customItemName}
              onChange={(e) => setCustomItemName(e.target.value)}
              placeholder="例: ドンペリ、シャンパン等"
              autoFocus
            />
            <TextField
              label="販売価格（税込）"
              type="number"
              fullWidth
              value={customItemPrice}
              onChange={(e) => setCustomItemPrice(e.target.value)}
              inputProps={{ min: 0, step: 100 }}
              placeholder="例: 30000"
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <TextField
                label="数量"
                type="number"
                value={customItemQuantity}
                onChange={(e) => setCustomItemQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                inputProps={{ min: 1 }}
                sx={{ width: 100 }}
              />
              {customItemPrice && (
                <Typography variant="body1" color="primary" fontWeight="bold">
                  小計: ¥{((parseFloat(customItemPrice) || 0) * customItemQuantity).toLocaleString()}
                </Typography>
              )}
            </Box>
            <Divider />
            <FormControlLabel
              control={
                <Switch
                  checked={customItemHasCost}
                  onChange={(e) => setCustomItemHasCost(e.target.checked)}
                />
              }
              label="原価を入力する"
            />
            {customItemHasCost && (
              <TextField
                label="原価（仕入れ値）"
                type="number"
                fullWidth
                value={customItemCost}
                onChange={(e) => setCustomItemCost(e.target.value)}
                inputProps={{ min: 0, step: 100 }}
                placeholder="例: 15000"
                helperText={customItemCost && customItemPrice
                  ? `粗利: ¥${((parseFloat(customItemPrice) || 0) - (parseFloat(customItemCost) || 0)).toLocaleString()} (${(((parseFloat(customItemPrice) || 0) - (parseFloat(customItemCost) || 0)) / (parseFloat(customItemPrice) || 1) * 100).toFixed(0)}%)`
                  : '配達代行費用なども含めた仕入れ原価'}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCustomItemDialogOpen(false)}>キャンセル</Button>
          <Button
            onClick={handleAddCustomItem}
            variant="contained"
            disabled={!customItemName.trim() || !customItemPrice || parseFloat(customItemPrice) <= 0}
          >
            カートに追加
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Checkout;
